import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import Pagination from '../components/Pagination';
import AddProductPanel from '../components/AddProductPanel';
import { useQzTray } from '../context/QzTrayContext';
import { printLaserSheet } from '../utils/qzTrayService';
import './ProductsPage.css';
import { compressImageFile } from '../utils/imageCompression';

function parseCsvFirstCell(lineRaw) {
  let line = String(lineRaw || '');
  if (!line) return '';
  if (line.charCodeAt(0) === 0xFEFF) line = line.slice(1);
  line = line.trim();
  if (!line) return '';
  const commaIndex = line.indexOf(',');
  let cell = commaIndex >= 0 ? line.slice(0, commaIndex) : line;
  cell = cell.trim();
  if (cell.startsWith('"') && cell.endsWith('"') && cell.length >= 2) {
    cell = cell.slice(1, -1).replace(/""/g, '"');
  }
  return cell.trim();
}

async function parseMyntraSkuCsvFile(file) {
  if (!file) throw new Error('Please choose a CSV file.');
  const text = await file.text();
  const lines = String(text || '')
    .split(/\r?\n/g)
    .map((line) => String(line || '').trim())
    .filter(Boolean);

  if (!lines.length) throw new Error('CSV is empty.');
  const header = parseCsvFirstCell(lines[0]);
  if (header !== 'sellerSkuCode') {
    throw new Error('Missing required CSV header: sellerSkuCode');
  }

  const skuList = [];
  const canonicalSeen = new Set();
  for (let i = 1; i < lines.length; i += 1) {
    const sellerSkuCode = parseCsvFirstCell(lines[i]).trim();
    if (!sellerSkuCode) continue;
    const canonical = sellerSkuCode.toUpperCase();
    if (canonicalSeen.has(canonical)) {
      throw new Error(`Duplicate SKU in CSV after trim: ${sellerSkuCode}`);
    }
    canonicalSeen.add(canonical);
    skuList.push(sellerSkuCode);
  }

  if (!skuList.length) throw new Error('SKU list is empty. Upload a CSV with sellerSkuCode values.');
  return skuList;
}

async function parseErrorMessage(response, fallbackMessage) {
  try {
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('application/json')) {
      const data = await response.json();
      return data?.message || fallbackMessage;
    }
    const text = await response.text();
    return text || fallbackMessage;
  } catch (_) {
    return fallbackMessage;
  }
}

function extractFilenameFromDisposition(headerValue, fallbackName = 'myntra-stock.csv') {
  const text = String(headerValue || '');
  const m = text.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  if (!m || !m[1]) return fallbackName;
  try {
    return decodeURIComponent(String(m[1] || '').trim());
  } catch (_) {
    return String(m[1] || '').trim() || fallbackName;
  }
}

const STATUS_LABELS = {
  photoshoot_pending: 'Pending',
  photoshoot_sent: 'Sent',
  photoshoot_received: 'Received',
  photoshoot_generating: 'AI Generating',
  photoshoot_review: 'AI Review',
  photoshoot_done: 'Photoshoot Done',
  details_generating: 'Details Generating',
  details_generated: 'Details Generated',
  lookbook_generating: 'Lookbook Generating',
  lookbook_generated: 'Lookbook Generated',
  ready_for_shopify: 'Ready for Shopify',
  shopify_publishing: 'Publishing',
  published: 'Published',
  publish_failed: 'Publish Failed',
};

const STATUS_COLORS = {
  photoshoot_pending: '#a0aec0',
  photoshoot_sent: '#805ad5',
  photoshoot_received: '#319795',
  photoshoot_generating: '#4c51bf',
  photoshoot_review: '#d69e2e',
  photoshoot_done: '#3182ce',
  details_generating: '#b7791f',
  details_generated: '#d69e2e',
  lookbook_generating: '#dd6b20',
  lookbook_generated: '#dd6b20',
  ready_for_shopify: '#2f855a',
  shopify_publishing: '#2b6cb0',
  published: '#38a169',
  publish_failed: '#e53e3e',
};

const INHOUSE_AI_STYLES = [
  { key: 'DRAPE_FOLDS', label: 'Soft Wave' },
  { key: 'ROSE_SWIRL', label: 'Diagonal Flow' },
  { key: 'FABRIC_EDITORIAL', label: 'Fabric Editorial' },
  { key: 'SCALE_FLATLAY', label: 'Scale Tape Photo' },
  { key: 'BASKET_LAY', label: 'Basket Lay' },
  { key: 'MANNEQUIN_DRAPE', label: 'Mannequin Drape' },
  { key: 'HANGER_DRAPE', label: 'Hanger Drape' },
  { key: 'ROLL_BOLT', label: 'Roll Bolt' },
];

const LOOKBOOK_V2_LABELS = {
  SAREE: 'Saree',
  ANARKALI: 'Anarkali',
  STRAIGHT_KURTA: 'Straight Kurta',
  ALINE_KURTA: 'A-Line Kurta',
  LEHENGA: 'Lehenga',
  SHARARA: 'Sharara',
  GHARARA: 'Gharara',
  PATIALA: 'Patiala',
  DHOTI_KURTA: 'Dhoti Kurta',
  ANGRAKHA: 'Angrakha',
  MAXI_DRESS: 'Maxi Dress',
  WRAP_DRESS: 'Wrap Dress',
  BODYCON_MIDI: 'Bodycon Midi',
  SLIP_DRESS: 'Slip Dress',
  SHIRT_DRESS: 'Shirt Dress',
  COORD_SET: 'Co-ord Set',
  CROP_COORD: 'Crop Co-ord',
  BODYCON_COORD: 'Bodycon Co-ord',
  JUMPSUIT: 'Jumpsuit',
  WRAP_COAT: 'Wrap Coat',
  CAPE_PALAZZO: 'Cape Palazzo',
  JACKET_KURTA: 'Jacket Kurta',
  ASYM_TUNIC: 'Asym Tunic',
  KAFTAN: 'Kaftan',
  CROP_DHOTI: 'Crop Dhoti',
  LONG_JACKET: 'Long Jacket',
  BLAZER_KURTA: 'Blazer Kurta',
  DRAPED_CAPE: 'Draped Cape',
  FABRIC_EDITORIAL: 'Fabric Editorial',
  KURTA_PANTS: 'Kurta Pants (2-Piece)',
  KURTA_PALAZZO: 'Kurta Palazzo (2-Piece)',
  PEPLUM_DHOTI: 'Peplum Dhoti (2-Piece)',
  KURTA_SALWAR: 'Kurta Salwar (2-Piece)',
  FABRIC_SET_EDITORIAL: 'Fabric Set Editorial',
};

function ProductsPage() {
  const { currentUser, isAdmin, canPerformAction } = useAuth();
  const { printSticker } = useQzTray() || {};
  const [activeTab, setActiveTab] = useState('catalog');
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [includeShopify, setIncludeShopify] = useState(true);
  const [includeZoho, setIncludeZoho] = useState(true);
  const [includeInventory, setIncludeInventory] = useState(true);
  const [missingShopify, setMissingShopify] = useState(false);
  const [missingZoho, setMissingZoho] = useState(false);
  const [missingInventory, setMissingInventory] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  const [showPricing, setShowPricing] = useState(false);
  const [pricingTestSkus, setPricingTestSkus] = useState('');
  const [pricingSettings, setPricingSettings] = useState({
    topNPerGroup: 10,
    bottomKPerGroup: 10,
    lookbackDays: 90,
    seasonalityWindowDays: 45,
    seasonalityYearsBack: 5,
    stepPctMin: 0.08,
    stepPctMax: 0.12,
    maxUpliftPct: 0.25,
    maxDiscountPct: 0.15,
    highStockTopPercent: 30,
    maxUpliftsPerRun: 200,
    maxDiscountsPerRun: 200,
    maxNormalizationsPerRun: 200,
    initialClampDone: false,
    charmPriceEnabled: true,
    neverDiscountProductTypes: [],
    label01BlockedProductTypes: [],
    labelAllBlockedProductTypes: [],
  });
  const [pricingBusy, setPricingBusy] = useState(false);
  const [pricingRun, setPricingRun] = useState({ runId: '', weekKey: '', proposedChanges: 0, counts: {} });
  const [pricingSkuDebug, setPricingSkuDebug] = useState(null);
  const [pricingSkuDebugBusy, setPricingSkuDebugBusy] = useState(false);
  const [pricingGidBackfillBusy, setPricingGidBackfillBusy] = useState(false);
  const [backfillAllBusy, setBackfillAllBusy] = useState(false);
  const [backfillAllProgress, setBackfillAllProgress] = useState({ updated: 0, cursor: null, hasNextPage: false });
  const [metafieldRun, setMetafieldRun] = useState({ runId: '', weekKey: '', proposedChanges: 0 });
  const [metafieldDiff, setMetafieldDiff] = useState([]);
  const [metafieldDiffBusy, setMetafieldDiffBusy] = useState(false);
  const [comboBusy, setComboBusy] = useState(false);
  const [lastPriceLabelRuns, setLastPriceLabelRuns] = useState([]);
  const [lastPriceLabelRunsBusy, setLastPriceLabelRunsBusy] = useState(false);
  const [stockSettings, setStockSettings] = useState({
    enabled: true,
    locationGid: 'gid://shopify/Location/38778798125',
    defaultMode: 'actual',
    productTypeModes: {},
    fabricRules: [],
  });
  const [stockBusy, setStockBusy] = useState(false);
  const [stockFabricRuleBusy, setStockFabricRuleBusy] = useState('');
  const [stockFabricSpecs, setStockFabricSpecs] = useState([]);
  const [stockPublications, setStockPublications] = useState([]);
  const [stockProductTypes, setStockProductTypes] = useState([]);
  const [stockRun, setStockRun] = useState({ runId: '', proposedChanges: 0, summary: {} });
  const [stockTestSkus, setStockTestSkus] = useState('');
  const [lastStockRuns, setLastStockRuns] = useState([]);
  const [lastStockRunsBusy, setLastStockRunsBusy] = useState(false);
  const [myntraStockFile, setMyntraStockFile] = useState(null);
  const [myntraStockBusy, setMyntraStockBusy] = useState(false);
  const [myntraLatestSkuBusy, setMyntraLatestSkuBusy] = useState(false);
  const [weightSyncBusy, setWeightSyncBusy] = useState(false);
  const [weightSyncPreview, setWeightSyncPreview] = useState(null);
  const canUseMyntraStock = canPerformAction('products.myntraStock');

  // ── New Products tab state ──
  const [newProducts, setNewProducts] = useState([]);
  const [newProductsLoading, setNewProductsLoading] = useState(false);
  const [newProductsError, setNewProductsError] = useState('');
  const [npBatchFilter, setNpBatchFilter] = useState('');
  const [npStatusFilter, setNpStatusFilter] = useState('');
  const [npActionBusy, setNpActionBusy] = useState('');
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [npBatchList, setNpBatchList] = useState([]);
  const [npBatchListLoading, setNpBatchListLoading] = useState(false);
  const [npPage, setNpPage] = useState(1);
  const [npPagination, setNpPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('User not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };
    return fetch(resolvedUrl, { ...options, headers });
  }, [currentUser]);

  // ── New Products fetch & actions ──
  const fetchNpBatches = useCallback(async () => {
    setNpBatchListLoading(true);
    try {
      const res = await authedFetch('/api/v1/new-products/batches');
      const data = await res.json();
      if (data.success) {
        setNpBatchList(data.batches || []);
        setNpBatchFilter(prev => {
          if (!prev && data.batches.length > 0) return data.batches[0].batchNumber;
          return prev;
        });
      }
    } catch {}
    finally { setNpBatchListLoading(false); }
  }, [authedFetch]);

  const fetchNewProducts = useCallback(async (page = 1) => {
    if (!npBatchFilter) { setNewProducts([]); setNpPagination({ page: 1, limit: 20, total: 0, totalPages: 0 }); return; }
    setNewProductsLoading(true);
    setNewProductsError('');
    try {
      const params = new URLSearchParams();
      params.set('batchNumber', npBatchFilter);
      if (npStatusFilter) params.set('status', npStatusFilter);
      params.set('page', String(page));
      params.set('limit', '20');
      const res = await authedFetch(`/api/v1/new-products?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setNewProducts(data.products);
        if (data.pagination) setNpPagination(data.pagination);
      }
      else setNewProductsError(data.message || 'Failed to load.');
    } catch (err) { setNewProductsError(`Failed to fetch new products: ${err.message || err}`); }
    finally { setNewProductsLoading(false); }
  }, [authedFetch, npStatusFilter, npBatchFilter]);

  const refreshSingleProduct = useCallback(async (sku) => {
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(sku)}`);
      const data = await res.json();
      if (data.success && data.product) {
        setNewProducts(prev => prev.map(p => p.sku === sku ? data.product : p));
        return data.product;
      }
    } catch {}
    return null;
  }, [authedFetch]);

  useEffect(() => {
    if (activeTab === 'newProducts') fetchNpBatches();
  }, [activeTab, fetchNpBatches]);

  useEffect(() => {
    if (activeTab === 'newProducts' && npBatchFilter) {
      setNpPage(1);
      fetchNewProducts(1);
    }
  }, [activeTab, npBatchFilter, npStatusFilter, fetchNewProducts]);

  useEffect(() => {
    if (activeTab === 'newProducts' && npBatchFilter) {
      fetchNewProducts(npPage);
    }
    // only react to page changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npPage]);

  const npBatches = useMemo(() => {
    return npBatchList.map(b => b.batchNumber);
  }, [npBatchList]);

  const [sendPhotoshootOpen, setSendPhotoshootOpen] = useState(false);
  const [sendPsBatch, setSendPsBatch] = useState('');
  const [sendPsSelected, setSendPsSelected] = useState({});
  const [sendPsBusy, setSendPsBusy] = useState(false);

  const pendingByBatch = useMemo(() => {
    const groups = {};
    newProducts.filter(p => p.status === 'photoshoot_pending').forEach(p => {
      const b = p.batchNumber || 'No Batch';
      if (!groups[b]) groups[b] = [];
      groups[b].push(p);
    });
    return groups;
  }, [newProducts]);

  const hasPendingInAnyBatch = useMemo(() => {
    return npBatchList.some(b => (b.statusCounts || {}).photoshoot_pending > 0);
  }, [npBatchList]);

  const [sendPsPendingProducts, setSendPsPendingProducts] = useState([]);
  const [sendPsFetchBusy, setSendPsFetchBusy] = useState(false);

  // ── Print Batch Labels (laser sheet) ──
  const [printLabelsOpen, setPrintLabelsOpen] = useState(false);
  const [printLabelsBatch, setPrintLabelsBatch] = useState('');
  const [printLabelsProducts, setPrintLabelsProducts] = useState([]);
  const [printLabelsSelected, setPrintLabelsSelected] = useState({});
  const [printLabelsFetchBusy, setPrintLabelsFetchBusy] = useState(false);
  const [printLabelsBusy, setPrintLabelsBusy] = useState(false);
  const [printLabelsStartSlot, setPrintLabelsStartSlot] = useState(1);

  const openPrintLabelsModal = () => {
    setPrintLabelsOpen(true);
    setPrintLabelsBatch('');
    setPrintLabelsSelected({});
    setPrintLabelsProducts([]);
    setPrintLabelsStartSlot(1);
  };

  const handlePrintLabelsBatchChange = async (batch) => {
    setPrintLabelsBatch(batch);
    setPrintLabelsSelected({});
    setPrintLabelsProducts([]);
    if (!batch) return;
    setPrintLabelsFetchBusy(true);
    try {
      const res = await authedFetch(`/api/v1/new-products?batchNumber=${encodeURIComponent(batch)}&limit=200`);
      const data = await res.json();
      if (data.success) {
        setPrintLabelsProducts(data.products || []);
        const sel = {};
        (data.products || []).forEach(p => { sel[p.sku] = true; });
        setPrintLabelsSelected(sel);
      }
    } catch {}
    finally { setPrintLabelsFetchBusy(false); }
  };

  const printLabelsSelectedCount = Object.values(printLabelsSelected).filter(Boolean).length;

  const handlePrintLabelsSubmit = () => {
    const selected = printLabelsProducts.filter(p => printLabelsSelected[p.sku]);
    if (selected.length === 0) return;
    setPrintLabelsBusy(true);
    try {
      const jobs = selected.map(p => {
        const pid = (p.pieceIds || [])[0] || '';
        return {
          payload: {
            line1: p.sku,
            line2: pid,
            line3: '',
            barcodeText: p.sku,
          },
          style: {},
        };
      });
      printLaserSheet({ jobs, startSlot: printLabelsStartSlot });
      const nextSlot = printLabelsStartSlot + selected.length;
      setPrintLabelsStartSlot(nextSlot > 33 ? 1 : nextSlot);
    } catch (e) {
      setError(e?.message || 'Failed to open print sheet.');
    } finally {
      setPrintLabelsBusy(false);
    }
  };

  const openSendPhotoshootModal = () => {
    setSendPhotoshootOpen(true);
    setSendPsBatch('');
    setSendPsSelected({});
    setSendPsPendingProducts([]);
  };

  const handleSendPsBatchChange = async (batch) => {
    setSendPsBatch(batch);
    setSendPsSelected({});
    setSendPsPendingProducts([]);
    if (!batch) return;
    setSendPsFetchBusy(true);
    try {
      const res = await authedFetch(`/api/v1/new-products?batchNumber=${encodeURIComponent(batch)}&status=photoshoot_pending&limit=100`);
      const data = await res.json();
      if (data.success) {
        setSendPsPendingProducts(data.products || []);
        const sel = {};
        (data.products || []).forEach(p => { sel[p.sku] = true; });
        setSendPsSelected(sel);
      }
    } catch {}
    finally { setSendPsFetchBusy(false); }
  };

  const handleSendPhotoshootSubmit = async () => {
    const skus = Object.keys(sendPsSelected).filter(k => sendPsSelected[k]);
    if (!sendPsBatch || skus.length === 0) return;
    setSendPsBusy(true);
    try {
      const res = await authedFetch('/api/v1/new-products/batch-send-photoshoot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchNumber: sendPsBatch, skus }),
      });
      const data = await res.json();
      if (data.success) {
        setSendPhotoshootOpen(false);
        fetchNpBatches();
        fetchNewProducts(npPage);
      } else setNewProductsError(data.message);
    } catch { setNewProductsError('Failed to mark sent.'); }
    finally { setSendPsBusy(false); }
  };

  const handleMarkReceived = async (sku) => {
    setNpActionBusy(`recv-${sku}`);
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(sku)}/mark-received`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) await refreshSingleProduct(sku);
      else setNewProductsError(data.message);
    } catch { setNewProductsError('Failed to mark received.'); }
    finally { setNpActionBusy(''); }
  };

  const handleShopifyPipelineAction = async (product) => {
    if (!product?.sku) return;
    const isPublish = product.status === 'ready_for_shopify'
      || product.status === 'published'
      || (product.status === 'publish_failed' && product.shopifyProductId);
    const confirmText = isPublish
      ? `${product.status === 'published' ? 'Update' : 'Publish'} ${product.sku} on Shopify now?`
      : `Prepare ${product.sku} for Shopify?\n\nThis can create a Shopify draft and generate AI product details. Final live publishing still requires Lookbook V2 approval.`;
    if (!window.confirm(confirmText)) return;
    setNpActionBusy(`shopify-${product.sku}`);
    setNewProductsError('');
    try {
      const endpoint = isPublish ? 'publish' : 'prepare';
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(product.sku)}/pipeline/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const responseText = await res.text();
      let data = null;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch (_) {
        data = null;
      }
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || responseText || `Shopify pipeline action failed (${res.status}).`);
      }
      if (data.warning) setNewProductsError(data.warning);
      if (data.message) setMessage(data.warning ? `${data.message} ${data.warning}` : data.message);
      const fresh = await refreshSingleProduct(product.sku);
      if (editPanelProduct?.sku === product.sku && fresh) {
        setEditPanelProduct(fresh);
      }
    } catch (err) {
      setNewProductsError(err?.message || 'Shopify pipeline action failed.');
    } finally {
      setNpActionBusy('');
    }
  };

  const handleSelectiveShopifyUpdate = async (product, scopes) => {
    if (!product?.shopifyProductId || !scopes?.length) return;
    const scopeLabels = { seo: 'SEO', tags: 'Tags', metadata: 'Metadata', details: 'Title & Description', lookbook: 'Lookbook' };
    const selected = scopes.map(s => scopeLabels[s] || s).join(', ');
    if (!window.confirm(`Update ${selected} on Shopify for ${product.sku}?`)) return;
    setNpActionBusy(`shopify-${product.sku}`);
    setNewProductsError('');
    try {
      const res = await authedFetch(`/api/v1/product-details/${encodeURIComponent(product.shopifyProductId)}/push-to-shopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scopes }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error(data?.message || `Update failed (${res.status}).`);
      setMessage(`Updated ${selected} on Shopify for ${product.sku}.`);
      const fresh = await refreshSingleProduct(product.sku);
      if (editPanelProduct?.sku === product.sku && fresh) setEditPanelProduct(fresh);
    } catch (err) {
      setNewProductsError(err?.message || 'Shopify selective update failed.');
    } finally {
      setNpActionBusy('');
    }
  };

  const [editPanelProduct, setEditPanelProduct] = useState(null);
  const [editBatchNumber, setEditBatchNumber] = useState('');
  const [editFabricPhotos, setEditFabricPhotos] = useState([]); // Array of { file, preview, url, path }
  const [editScaleReferencePhoto, setEditScaleReferencePhoto] = useState(null); // { file, preview, url, path }
  const [editSheerReferencePhoto, setEditSheerReferencePhoto] = useState(null); // { file, preview, url, path }
  const [editBusy, setEditBusy] = useState(false);
  const [designEditSku, setDesignEditSku] = useState('');
  const [designEditValue, setDesignEditValue] = useState('');
  const [designEditBusy, setDesignEditBusy] = useState(false);
  const [newProductDesignFile, setNewProductDesignFile] = useState('');

  const [shopifyUpdateScopes, setShopifyUpdateScopes] = useState(['seo', 'tags', 'metadata', 'details']);
  const [photoUploadProgress, setPhotoUploadProgress] = useState(null);
  const [inhouseAiBusy, setInhouseAiBusy] = useState(false);
  const [inhouseAiSelectedStyles, setInhouseAiSelectedStyles] = useState([]);
  const [aiStyleActionBusy, setAiStyleActionBusy] = useState('');
  const [newProductWorkflow, setNewProductWorkflow] = useState(null);
  const [workflowBusy, setWorkflowBusy] = useState('');
  const [workflowError, setWorkflowError] = useState('');

  const downloadPhoto = async (url, filename) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch { alert('Failed to download image.'); }
  };

  const [activeSwatchStyleKeys, setActiveSwatchStyleKeys] = useState([]);
  const [swatchPromptsOpen, setSwatchPromptsOpen] = useState(false);
  const [swatchStyles, setSwatchStyles] = useState([]);
  const [swatchPreamble, setSwatchPreamble] = useState('');
  const [swatchDefaultPreamble, setSwatchDefaultPreamble] = useState('');
  const [swatchPromptsBusy, setSwatchPromptsBusy] = useState(false);

  const openSwatchPrompts = async () => {
    setSwatchPromptsOpen(true);
    setSwatchPromptsBusy(true);
    try {
      const res = await authedFetch('/api/v1/new-products/swatch-prompts');
      const data = await res.json();
      if (data.success) {
        setSwatchStyles(Array.isArray(data.styles) ? data.styles : []);
        setSwatchPreamble(data.preamble || '');
        setSwatchDefaultPreamble(data.defaultPreamble || '');
      }
    } catch {}
    finally { setSwatchPromptsBusy(false); }
  };

  const handleSaveSwatchPrompts = async () => {
    const valid = swatchStyles.filter(s => s.key?.trim() && s.prompt?.trim());
    if (valid.length === 0) { alert('Add at least one style with a prompt.'); return; }
    setSwatchPromptsBusy(true);
    try {
      const res = await authedFetch('/api/v1/new-products/swatch-prompts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ styles: valid, preamble: swatchPreamble }),
      });
      const data = await res.json();
      if (data.success) {
        alert('Prompts saved successfully!');
        setSwatchPromptsOpen(false);
      } else {
        alert(data.message || 'Failed to save prompts.');
      }
    } catch { alert('Failed to save prompts.'); }
    finally { setSwatchPromptsBusy(false); }
  };

  const addSwatchStyle = () => {
    setSwatchStyles(prev => [...prev, { key: '', label: '', prompt: '', autoGenerate: true }]);
  };

  const updateSwatchStyle = (index, field, value) => {
    setSwatchStyles(prev => prev.map((s, i) => {
      if (i !== index) return s;
      const updated = { ...s, [field]: value };
      if (field === 'label') updated.key = value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_');
      return updated;
    }));
  };

  const removeSwatchStyle = (index) => {
    setSwatchStyles(prev => prev.filter((_, i) => i !== index));
  };

  const moveSwatchStyle = (index, dir) => {
    setSwatchStyles(prev => {
      const arr = [...prev];
      const target = index + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return arr;
    });
  };

  const [photoshootPanelProduct, setPhotoshootPanelProduct] = useState(null);
  const [psUploadBusy, setPsUploadBusy] = useState(false);

  const canEdit = canPerformAction('addProduct.edit');
  const canDelete = canPerformAction('addProduct.delete');
  const canUploadPhotoshoot = canPerformAction('addProduct.uploadPhotoshoot');
  const canGenerateLookbook = canPerformAction('addProduct.generateLookbook');
  const canPushShopify = canPerformAction('addProduct.pushShopify');

  const openEditPanel = async (product) => {
    setEditPanelProduct(product);
    setEditBatchNumber(product.batchNumber || '');
    setNewProductDesignFile('');
    authedFetch('/api/v1/production-design-files/resolve', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skus: [product.sku] }),
    }).then(async r => {
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.message || 'Could not load master design file number.');
      return data;
    }).then(data => {
      setNewProductDesignFile(data.overrides?.[product.sku] || '');
    }).catch(err => setNewProductsError(err.message));

    const existing = Array.isArray(product.fabricPhotos) ? product.fabricPhotos : [];
    if (existing.length === 0 && product.fabricPhotoUrl && product.fabricPhotoPath) {
      setEditFabricPhotos([{ url: product.fabricPhotoUrl, path: product.fabricPhotoPath, preview: product.fabricPhotoUrl }]);
    } else {
      setEditFabricPhotos(existing.map(p => ({ ...p, preview: p.url })));
    }
    const scaleRef = product.scaleReferencePhoto || null;
    setEditScaleReferencePhoto(scaleRef?.url && scaleRef?.path ? { ...scaleRef, preview: scaleRef.url } : null);
    const sheerRef = product.sheerReferencePhoto || null;
    setEditSheerReferencePhoto(sheerRef?.url && sheerRef?.path ? { ...sheerRef, preview: sheerRef.url } : null);
    setInhouseAiSelectedStyles([]);
    authedFetch('/api/v1/new-products/swatch-prompts').then(r => r.json()).then(data => {
      if (data.success && Array.isArray(data.styles)) setActiveSwatchStyleKeys(data.styles.map(s => ({ key: s.key, label: s.label })));
    }).catch(() => {});
    setNewProductWorkflow(null);
    setWorkflowError('');
    refreshSingleProduct(product.sku).then(fresh => {
      if (fresh) setEditPanelProduct(fresh);
    });
  };

  const handleEditSubmit = async () => {
    if (!editPanelProduct) return;
    setEditBusy(true);
    try {
      const fabricPhotos = editFabricPhotos.filter(p => p.url && p.path).map(p => ({ url: p.url, path: p.path }));
      const scaleReferencePhoto = editScaleReferencePhoto?.url ? { url: editScaleReferencePhoto.url, path: editScaleReferencePhoto.path } : null;
      const sheerReferencePhoto = editSheerReferencePhoto?.url ? { url: editSheerReferencePhoto.url, path: editSheerReferencePhoto.path } : null;
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(editPanelProduct.sku)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchNumber: editBatchNumber, fabricPhotos, scaleReferencePhoto, sheerReferencePhoto }),
      });
      const data = await res.json();
      if (data.success) {
        const fresh = await refreshSingleProduct(editPanelProduct.sku);
        if (fresh) setEditPanelProduct(fresh);
      } else {
        setNewProductsError(data.message);
      }
    } catch (err) {
      setNewProductsError(err?.message || 'Failed to save.');
    } finally {
      setEditBusy(false);
    }
  };

  const saveDesignFile = async (sku, value) => {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized && !/^[A-Z0-9_-]{1,80}$/.test(normalized)) {
      setError('Enter a file number without an extension or path.');
      setNewProductsError('Enter a file number without an extension or path.');
      return false;
    }
    setDesignEditBusy(true);
    try {
      const response = await authedFetch(`/api/v1/production-design-files/${encodeURIComponent(sku)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ masterDesignFileNumber: normalized }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Save failed.');
      setProducts(prev => prev.map(product => product.sku === sku ? { ...product, masterDesignFileNumber: normalized } : product));
      setMessage(`Master design file number saved for ${sku}.`);
      setNewProductsError('');
      return true;
    } catch (err) {
      setError(err.message);
      setNewProductsError(err.message);
      return false;
    } finally {
      setDesignEditBusy(false);
    }
  };

  const uploadSinglePhoto = async (file, sku) => {
    const compressed = await compressImageFile(file);
    const reader = new FileReader();
    const base64 = await new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(compressed);
    });
    const res = await authedFetch('/api/v1/add-product/upload-fabric-photo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64Image: base64, mimeType: compressed.type, sku }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Upload failed.');
    return { url: data.url, path: data.path, preview: data.url };
  };

  const autoUploadPhotos = async (newFiles, photoType = 'fabric') => {
    if (!editPanelProduct?.sku || !newFiles.length) return;
    const sku = editPanelProduct.sku;
    setPhotoUploadProgress({ current: 0, total: newFiles.length, label: 'Compressing...' });
    setNewProductsError('');
    try {
      const uploaded = [];
      for (let i = 0; i < newFiles.length; i++) {
        setPhotoUploadProgress({ current: i + 1, total: newFiles.length, label: `Uploading ${i + 1}/${newFiles.length}...` });
        const result = await uploadSinglePhoto(newFiles[i], sku);
        uploaded.push(result);
      }
      setPhotoUploadProgress({ current: newFiles.length, total: newFiles.length, label: 'Saving...' });

      let fabricPhotos, scaleReferencePhoto, sheerReferencePhoto;
      if (photoType === 'fabric') {
        const existing = editFabricPhotos.filter(p => p.url && p.path).map(p => ({ url: p.url, path: p.path }));
        fabricPhotos = [...existing, ...uploaded.map(u => ({ url: u.url, path: u.path }))];
        scaleReferencePhoto = editScaleReferencePhoto?.url ? { url: editScaleReferencePhoto.url, path: editScaleReferencePhoto.path } : null;
        sheerReferencePhoto = editSheerReferencePhoto?.url ? { url: editSheerReferencePhoto.url, path: editSheerReferencePhoto.path } : null;
        setEditFabricPhotos(prev => [...prev.filter(p => p.url && p.path), ...uploaded]);
      } else if (photoType === 'scale') {
        fabricPhotos = editFabricPhotos.filter(p => p.url && p.path).map(p => ({ url: p.url, path: p.path }));
        scaleReferencePhoto = { url: uploaded[0].url, path: uploaded[0].path };
        sheerReferencePhoto = editSheerReferencePhoto?.url ? { url: editSheerReferencePhoto.url, path: editSheerReferencePhoto.path } : null;
        setEditScaleReferencePhoto(uploaded[0]);
      } else if (photoType === 'sheer') {
        fabricPhotos = editFabricPhotos.filter(p => p.url && p.path).map(p => ({ url: p.url, path: p.path }));
        scaleReferencePhoto = editScaleReferencePhoto?.url ? { url: editScaleReferencePhoto.url, path: editScaleReferencePhoto.path } : null;
        sheerReferencePhoto = { url: uploaded[0].url, path: uploaded[0].path };
        setEditSheerReferencePhoto(uploaded[0]);
      }

      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(sku)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batchNumber: editBatchNumber,
          fabricPhotos,
          scaleReferencePhoto,
          sheerReferencePhoto,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const fresh = await refreshSingleProduct(sku);
        if (fresh) setEditPanelProduct(fresh);
      } else {
        setNewProductsError(data.message || 'Failed to save photos.');
      }
    } catch (err) {
      setNewProductsError(err?.message || 'Photo upload failed.');
    } finally {
      setPhotoUploadProgress(null);
    }
  };

  const autoSavePhotos = async (fabricPhotos, scaleRef, sheerRef) => {
    if (!editPanelProduct?.sku) return;
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(editPanelProduct.sku)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batchNumber: editBatchNumber,
          fabricPhotos: fabricPhotos.filter(p => p.url && p.path).map(p => ({ url: p.url, path: p.path })),
          scaleReferencePhoto: scaleRef?.url ? { url: scaleRef.url, path: scaleRef.path } : null,
          sheerReferencePhoto: sheerRef?.url ? { url: sheerRef.url, path: sheerRef.path } : null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const fresh = await refreshSingleProduct(editPanelProduct.sku);
        if (fresh) setEditPanelProduct(fresh);
      }
    } catch {}
  };

  const handleRemoveFabricPhoto = (index) => {
    setEditFabricPhotos(prev => {
      const updated = prev.filter((_, i) => i !== index);
      autoSavePhotos(updated, editScaleReferencePhoto, editSheerReferencePhoto);
      return updated;
    });
  };

  const handleRemoveScaleRef = () => {
    setEditScaleReferencePhoto(null);
    autoSavePhotos(editFabricPhotos, null, editSheerReferencePhoto);
  };

  const handleRemoveSheerRef = () => {
    setEditSheerReferencePhoto(null);
    autoSavePhotos(editFabricPhotos, editScaleReferencePhoto, null);
  };

  const handleGenerateInhouseAi = async () => {
    if (!editPanelProduct) return;
    const generatedStyleKeys = new Set((editPanelProduct.photoshootImages || [])
      .map(img => String(img.style || '').toUpperCase())
      .filter(Boolean));
    const stylesToGenerate = inhouseAiSelectedStyles.filter(style => !generatedStyleKeys.has(style));
    if (stylesToGenerate.length === 0) {
      alert('Please select at least one presentation layout style.');
      return;
    }
    setInhouseAiBusy(true);
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(editPanelProduct.sku)}/generate-inhouse-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ styles: stylesToGenerate }),
      });
      const data = await res.json();
      if (data.success) {
        // Refresh product display immediately
        const freshRes = await authedFetch(`/api/v1/new-products/${encodeURIComponent(editPanelProduct.sku)}`);
        const freshData = await freshRes.json();
        if (freshData.success) {
          setEditPanelProduct(freshData.product);
        }
        setInhouseAiSelectedStyles([]);
        await refreshSingleProduct(editPanelProduct.sku);
        alert('AI Photoshoot images generated successfully!');
      } else {
        alert(data.message || 'Failed to generate AI photoshoot.');
      }
    } catch (err) {
      alert(err?.message || 'Failed to generate AI photoshoot.');
    } finally {
      setInhouseAiBusy(false);
    }
  };

  const refreshEditPanelProduct = async (sku) => {
    const freshRes = await authedFetch(`/api/v1/new-products/${encodeURIComponent(sku)}`);
    const freshData = await freshRes.json();
    if (freshData.success) {
      setEditPanelProduct(freshData.product);
      return freshData.product;
    }
    throw new Error(freshData.message || 'Failed to refresh product.');
  };

  const loadNewProductWorkflow = async (sku) => {
    if (!sku) return null;
    setWorkflowError('');
    const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(sku)}/workflow`);
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Failed to load workflow.');
    setNewProductWorkflow(data);
    return data;
  };

  const refreshNewProductWorkflow = async (sku = editPanelProduct?.sku) => {
    if (!sku) return;
    try {
      await loadNewProductWorkflow(sku);
    } catch (err) {
      setWorkflowError(err?.message || 'Failed to load workflow.');
    }
  };

  useEffect(() => {
    if (!editPanelProduct?.sku) return;
    refreshNewProductWorkflow(editPanelProduct.sku);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPanelProduct?.sku]);

  const handleGenerateProductDetails = async () => {
    if (!editPanelProduct?.sku) return;
    setWorkflowBusy('details-generate');
    setWorkflowError('');
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(editPanelProduct.sku)}/pipeline/prepare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to generate product details.');
      const fresh = await refreshSingleProduct(editPanelProduct.sku);
      if (fresh) setEditPanelProduct(fresh);
      await refreshNewProductWorkflow(editPanelProduct.sku);
    } catch (err) {
      setWorkflowError(err?.message || 'Failed to generate product details.');
    } finally {
      setWorkflowBusy('');
    }
  };

  const handleGenerateLookbookV2 = async (outfitCode) => {
    const wf = newProductWorkflow || {};
    const lb = wf.lookbook || {};
    if (!editPanelProduct?.sku || !wf.shopifyProductId || !lb.fabricSpecId || !outfitCode) return;
    setWorkflowBusy(`lookbook-generate-${outfitCode}`);
    setWorkflowError('');
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fabricSpecId: lb.fabricSpecId,
          outfitCode,
          productId: wf.shopifyProductId,
          variantId: lb.variantId || undefined,
          ownerSku: editPanelProduct.sku,
          productType: lb.type || newProductWorkflow?.productDetails?.productType || undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to generate Lookbook image.');
      await refreshNewProductWorkflow(editPanelProduct.sku);
    } catch (err) {
      setWorkflowError(err?.message || 'Failed to generate Lookbook image.');
    } finally {
      setWorkflowBusy('');
    }
  };

  const handleReviewLookbookV2 = async (outfitCode, action) => {
    const wf = newProductWorkflow || {};
    const lb = wf.lookbook || {};
    if (!editPanelProduct?.sku || !wf.shopifyProductId || !lb.fabricSpecId || !outfitCode || !action) return;
    setWorkflowBusy(`lookbook-${action}-${outfitCode}`);
    setWorkflowError('');
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${encodeURIComponent(lb.fabricSpecId)}/outfit/${encodeURIComponent(outfitCode)}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: wf.shopifyProductId,
          variantId: lb.variantId || '',
          ownerSku: editPanelProduct.sku,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || `Failed to ${action} Lookbook image.`);
      await authedFetch(`/api/v1/new-products/${encodeURIComponent(editPanelProduct.sku)}/pipeline/prepare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }).catch(() => null);
      const fresh = await refreshSingleProduct(editPanelProduct.sku);
      if (fresh) setEditPanelProduct(fresh);
      await refreshNewProductWorkflow(editPanelProduct.sku);
    } catch (err) {
      setWorkflowError(err?.message || `Failed to ${action} Lookbook image.`);
    } finally {
      setWorkflowBusy('');
    }
  };

  const handleRegenerateInhouseAiStyle = async (styleKey) => {
    if (!editPanelProduct || !styleKey) return;
    setAiStyleActionBusy(`regen-${styleKey}`);
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(editPanelProduct.sku)}/generate-inhouse-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ styles: [styleKey], regenerate: true }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to regenerate AI style.');
      const fresh = await refreshSingleProduct(editPanelProduct.sku);
      if (fresh) setEditPanelProduct(fresh);
    } catch (err) {
      setNewProductsError(err?.message || 'Failed to regenerate AI style.');
    } finally {
      setAiStyleActionBusy('');
    }
  };

  const [lookbookGenerating, setLookbookGenerating] = useState(false);
  const handleGenerateLookbook = async () => {
    if (!editPanelProduct) return;
    setLookbookGenerating(true);
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(editPanelProduct.sku)}/generate-lookbook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to generate lookbook.');
      const fresh = await refreshSingleProduct(editPanelProduct.sku);
      if (fresh) setEditPanelProduct(fresh);
      setNewProductsError('');
    } catch (err) {
      setNewProductsError(err?.message || 'Failed to generate lookbook images.');
    } finally {
      setLookbookGenerating(false);
    }
  };

  const handleReviewInhouseAiStyle = async (styleKey, action) => {
    if (!editPanelProduct || !styleKey || !action) return;
    setAiStyleActionBusy(`${action}-${styleKey}`);
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(editPanelProduct.sku)}/ai-styles/${encodeURIComponent(styleKey)}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || `Failed to ${action} AI style.`);
      setInhouseAiSelectedStyles(prev => prev.filter(s => s !== styleKey));
      const fresh = await refreshSingleProduct(editPanelProduct.sku);
      if (fresh) setEditPanelProduct(fresh);
    } catch (err) {
      setNewProductsError(err?.message || `Failed to ${action} AI style.`);
    } finally {
      setAiStyleActionBusy('');
    }
  };

  const handleDeleteProduct = async (sku) => {
    if (!window.confirm(`Delete product ${sku} and its inventory pieces? This cannot be undone.`)) return;
    setNpActionBusy(`del-${sku}`);
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(sku)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setNewProducts(prev => prev.filter(p => p.sku !== sku));
        if (editPanelProduct?.sku === sku) setEditPanelProduct(null);
        fetchNpBatches();
      } else setNewProductsError(data.message);
    } catch { setNewProductsError('Failed to delete product.'); }
    finally { setNpActionBusy(''); }
  };

  const [psUploadProgress, setPsUploadProgress] = useState('');

  const handlePhotoshootUpload = async (files) => {
    if (!photoshootPanelProduct || !files || files.length === 0) return;
    setPsUploadBusy(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setPsUploadProgress(`Uploading ${i + 1} of ${files.length}...`);
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(photoshootPanelProduct.sku)}/photoshoot-images`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ base64Image: base64, mimeType: file.type }),
        });
        const data = await res.json();
        if (!data.success) { setNewProductsError(data.message); break; }
      }
      const updatedRes = await authedFetch(`/api/v1/new-products/${encodeURIComponent(photoshootPanelProduct.sku)}`);
      const updatedData = await updatedRes.json();
      if (updatedData.success) {
        setPhotoshootPanelProduct(updatedData.product);
        refreshSingleProduct(photoshootPanelProduct.sku);
      }
    } catch { setNewProductsError('Failed to upload photoshoot image(s).'); }
    finally { setPsUploadBusy(false); setPsUploadProgress(''); }
  };

  const handleDeletePhotoshootImage = async (index) => {
    if (!photoshootPanelProduct) return;
    setPsUploadBusy(true);
    try {
      const res = await authedFetch(`/api/v1/new-products/${encodeURIComponent(photoshootPanelProduct.sku)}/photoshoot-images/${index}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        const updatedRes = await authedFetch(`/api/v1/new-products/${encodeURIComponent(photoshootPanelProduct.sku)}`);
        const updatedData = await updatedRes.json();
        if (updatedData.success) {
          setPhotoshootPanelProduct(updatedData.product);
          refreshSingleProduct(photoshootPanelProduct.sku);
        }
      } else setNewProductsError(data.message);
    } catch { setNewProductsError('Failed to delete image.'); }
    finally { setPsUploadBusy(false); }
  };

  const isUploadWindowOpen = (product) => {
    if (!product.photoshootFirstUploadAt) return ['photoshoot_received', 'photoshoot_done'].includes(product.status);
    const firstUpload = product.photoshootFirstUploadAt?._seconds
      ? new Date(product.photoshootFirstUploadAt._seconds * 1000)
      : new Date(product.photoshootFirstUploadAt);
    const hoursSince = (Date.now() - firstUpload.getTime()) / (1000 * 60 * 60);
    return hoursSince < 24;
  };

  const canRunShopifyPipeline = (product) => {
    if (!product || product.status === 'shopify_publishing') return false;
    if (product.status === 'published') return Boolean(product.shopifyProductId);
    if (product.status === 'ready_for_shopify') return true;
    if (['details_generating', 'lookbook_generating', 'details_generated', 'lookbook_generated', 'publish_failed'].includes(product.status)) return true;
    if (product.status !== 'photoshoot_done') return false;
    const reviewMap = product.aiPhotoshootReview || {};
    const styleKeys = [...new Set((product.photoshootImages || []).map(img => String(img.style || '').toUpperCase()).filter(Boolean))];
    if (styleKeys.length === 0) return false;
    return styleKeys.every(key => ['approved', 'skipped'].includes(reviewMap[key]));
  };

  const getShopifyPipelineButtonLabel = (product) => {
    const wasPublished = product.status === 'published' || product.publishedAt || product.lastShopifySyncAt;
    if (npActionBusy === `shopify-${product.sku}`) {
      return ['ready_for_shopify', 'published'].includes(product.status) || (product.status === 'publish_failed' && product.shopifyProductId)
        ? (wasPublished ? 'Updating...' : 'Publishing...')
        : 'Preparing...';
    }
    if (product.status === 'published') return 'Update Shopify';
    if (product.status === 'ready_for_shopify') return wasPublished ? 'Update Shopify' : 'Push to Shopify';
    if (product.status === 'publish_failed') return 'Retry Publish';
    if (product.status === 'lookbook_generating') return 'Check Readiness';
    if (product.shopifyProductId) return 'Continue Prep';
    return 'Prepare for Shopify';
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page on new search
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const fetchProducts = useCallback(async (page, search) => {
    if (!currentUser) return;
    setIsLoading(true);
    setError('');
    setMessage('');
    try {
      const token = await currentUser.getIdToken();
      const normalizedSearch = search ? search.trim().toUpperCase() : '';
      const params = new URLSearchParams({
        page: String(page),
        limit: String(itemsPerPage),
        includeShopify: String(includeShopify),
        includeZoho: String(includeZoho),
        includeInventory: String(includeInventory),
        missingShopify: String(missingShopify),
        missingZoho: String(missingZoho),
        missingInventory: String(missingInventory),
      });
      if (normalizedSearch) params.append('search', normalizedSearch);
      const url = `/api/v1/products/merged?${params.toString()}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        let message = 'Failed to fetch products.';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errData = await response.json();
            message = errData.message || message;
          } else {
            const text = await response.text();
            if (text) message = `${message} (${text.slice(0, 120)}${text.length > 120 ? '...' : ''})`;
          }
        } catch (_) {
          // ignore parse errors, keep default message
        }
        throw new Error(message);
      }
      const data = await response.json();
      setProducts(data.products || []);
      setTotalItems(data.totalCount || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, includeShopify, includeZoho, includeInventory, missingShopify, missingZoho, missingInventory]);

  const loadPricingSettings = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const resp = await authedFetch('/api/v1/pricing-automation/settings');
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to load pricing settings.');
      setPricingSettings(data.settings || pricingSettings);
    } catch (e) {
      setError(e.message || 'Failed to load pricing settings.');
    }
  }, [authedFetch, isAdmin, pricingSettings]);

  const loadStockSettings = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const resp = await authedFetch('/api/v1/stock-publish/settings');
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to load stock settings.');
      setStockSettings((prev) => data.settings || prev);
    } catch (e) {
      setError(e.message || 'Failed to load stock settings.');
    }
  }, [authedFetch, isAdmin]);

  const loadStockFabricRuleOptions = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const resp = await authedFetch('/api/v1/stock-publish/fabric-rule-options');
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to load fabric rule options.');
      setStockFabricSpecs(Array.isArray(data.specs) ? data.specs : []);
      setStockPublications(Array.isArray(data.publications) ? data.publications : []);
    } catch (e) {
      setError(e.message || 'Failed to load fabric rule options.');
    }
  }, [authedFetch, isAdmin]);

  const loadStockProductTypes = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const resp = await authedFetch('/api/v1/products/types');
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to load product types.');
      setStockProductTypes(Array.isArray(data.productTypes) ? data.productTypes : []);
    } catch (e) {
      setError(e.message || 'Failed to load product types.');
    }
  }, [authedFetch, isAdmin]);

  const loadLastPriceLabelRuns = useCallback(async () => {
    if (!isAdmin) return;
    setLastPriceLabelRunsBusy(true);
    try {
      const resp = await authedFetch('/api/v1/price-labels/last-runs?limit=5');
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to load last runs.');
      const runs = Array.isArray(data.runs) ? data.runs : [];
      setLastPriceLabelRuns(runs);

      // If the user refreshed the page while a run is still "running", the local runIds may be empty,
      // which disables the Apply button. Auto-select the most recent running (or latest) runIds.
      const hasLocal = Boolean(String(pricingRun?.runId || '').trim()) && Boolean(String(metafieldRun?.runId || '').trim());
      if (!hasLocal && runs.length > 0) {
        const running = runs.find((r) => String(r?.status || '').toLowerCase() === 'running');
        const pick = running || runs[0];
        const p = String(pick?.pricing?.runId || '').trim();
        const l = String(pick?.labels?.runId || '').trim();
        if (p && l) {
          setPricingRun((prev) => ({ ...prev, runId: p }));
          setMetafieldRun((prev) => ({ ...prev, runId: l }));
          setMessage(`Loaded last run IDs. Pricing: ${p}. Labels: ${l}.`);
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to load last runs.');
    } finally {
      setLastPriceLabelRunsBusy(false);
    }
  }, [authedFetch, isAdmin, pricingRun?.runId, metafieldRun?.runId]);

  const loadLastStockRuns = useCallback(async () => {
    if (!isAdmin) return;
    setLastStockRunsBusy(true);
    try {
      const resp = await authedFetch('/api/v1/stock-publish/last-runs?limit=5');
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to load stock runs.');
      const runs = Array.isArray(data.runs) ? data.runs : [];
      setLastStockRuns(runs);
      if (!String(stockRun?.runId || '').trim() && runs.length > 0) {
        const pick = runs[0];
        const runId = String(pick?.runId || '').trim();
        if (runId) {
          setStockRun((prev) => ({ ...prev, runId }));
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to load stock runs.');
    } finally {
      setLastStockRunsBusy(false);
    }
  }, [authedFetch, isAdmin, stockRun?.runId]);

  useEffect(() => {
    if (activeTab !== 'stockPublish' || !isAdmin) return;
    loadStockSettings();
    loadStockFabricRuleOptions();
    loadStockProductTypes();
    loadLastStockRuns();
  }, [activeTab, isAdmin, loadLastStockRuns, loadStockFabricRuleOptions, loadStockProductTypes, loadStockSettings]);

  const savePricingSettings = useCallback(async () => {
    setPricingBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch('/api/v1/pricing-automation/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: pricingSettings }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to save pricing settings.');
      setPricingSettings(data.settings || pricingSettings);
      setMessage('Pricing settings saved.');
    } catch (e) {
      setError(e.message || 'Failed to save pricing settings.');
    } finally {
      setPricingBusy(false);
    }
  }, [authedFetch, pricingSettings]);

  const saveStockSettings = useCallback(async () => {
    setStockBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch('/api/v1/stock-publish/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: stockSettings }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to save stock settings.');
      setStockSettings(data.settings || stockSettings);
      setMessage('Stock settings saved.');
    } catch (e) {
      setError(e.message || 'Failed to save stock settings.');
    } finally {
      setStockBusy(false);
    }
  }, [authedFetch, stockSettings]);

  const updateStockFabricRule = useCallback((ruleId, patch) => {
    setStockSettings((prev) => ({
      ...prev,
      fabricRules: (Array.isArray(prev.fabricRules) ? prev.fabricRules : []).map((rule) => (
        String(rule.id || '') === String(ruleId || '') ? { ...rule, ...patch } : rule
      )),
    }));
  }, []);

  const addStockFabricRule = useCallback(() => {
    const id = `rule_${Date.now()}`;
    setStockSettings((prev) => ({
      ...prev,
      fabricRules: [
        ...(Array.isArray(prev.fabricRules) ? prev.fabricRules : []),
        {
          id,
          enabled: true,
          productType: '',
          quality: '',
          fabricNameForOffice: '',
          stockPublishMode: 'inherit',
          productStatus: 'no_change',
          inventoryPolicy: 'no_change',
          publicationMode: 'no_change',
          publicationIds: [],
          minQty: '',
          maxQty: '',
          priority: (Array.isArray(prev.fabricRules) ? prev.fabricRules.length : 0) + 1,
        },
      ],
    }));
  }, []);

  const removeStockFabricRule = useCallback((ruleId) => {
    setStockSettings((prev) => ({
      ...prev,
      fabricRules: (Array.isArray(prev.fabricRules) ? prev.fabricRules : []).filter((rule) => String(rule.id || '') !== String(ruleId || '')),
    }));
  }, []);

  const applyStockFabricSpecToRule = useCallback((ruleId, specId) => {
    const spec = stockFabricSpecs.find((item) => String(item.id || '') === String(specId || ''));
    if (!spec) return;
    updateStockFabricRule(ruleId, {
      productType: spec.productType || '',
      quality: spec.quality || '',
      fabricNameForOffice: spec.fabricNameForOffice || '',
    });
  }, [stockFabricSpecs, updateStockFabricRule]);

  const parseStockTestSkuFilter = useCallback(() => (
    String(stockTestSkus || '')
      .split(/[,\n]/g)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)
  ), [stockTestSkus]);

  const calculateAndApplyStockRule = useCallback(async (ruleId = '', skuFilterOverride = null) => {
    const label = ruleId ? 'fabric rule' : 'all fabric rules';
    const skuFilter = Array.isArray(skuFilterOverride) ? skuFilterOverride : parseStockTestSkuFilter();
    const skuScopeText = skuFilter.length ? ` for ${skuFilter.length} test SKU${skuFilter.length === 1 ? '' : 's'}` : '';
    if (!window.confirm(`Calculate and push ${label}${skuScopeText} to Shopify now?`)) return;
    setStockFabricRuleBusy(ruleId || 'all');
    setError('');
    setMessage('');
    try {
      const saveResp = await authedFetch('/api/v1/stock-publish/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: stockSettings }),
      });
      const saveData = await saveResp.json();
      if (!saveResp.ok || saveData?.success === false) throw new Error(saveData?.message || 'Failed to save stock settings.');
      setStockSettings(saveData.settings || stockSettings);

      const calcResp = await authedFetch('/api/v1/stock-publish/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fabricRuleId: ruleId || '', fabricRulesOnly: true, skuFilter }),
      });
      const calcData = await calcResp.json();
      if (!calcResp.ok || calcData?.success === false) throw new Error(calcData?.message || 'Failed to calculate stock publish.');

      const runId = String(calcData.runId || '').trim();
      setStockRun({
        runId,
        proposedChanges: Number(calcData.proposedChanges || 0),
        summary: calcData.summary || {},
      });
      if (!runId) throw new Error('Stock publish run was not created.');

      const applyResp = await authedFetch('/api/v1/stock-publish/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ runId }),
      });
      const applyData = await applyResp.json();
      if (!applyResp.ok || applyData?.success === false) throw new Error(applyData?.message || 'Failed to apply stock publish.');
      setMessage(applyData?.message || `Applied ${Number(applyData?.appliedChanges || 0)} Shopify updates.`);
      loadLastStockRuns();
    } catch (e) {
      setError(e.message || `Failed to push ${label}.`);
    } finally {
      setStockFabricRuleBusy('');
    }
  }, [authedFetch, loadLastStockRuns, parseStockTestSkuFilter, stockSettings]);

  const debugPricingSku = useCallback(async () => {
    const skuFilter = String(pricingTestSkus || '')
      .split(/[,\n]/g)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    const sku = skuFilter.length ? skuFilter[0] : '';
    if (!sku) {
      setError('Enter a Test SKU first.');
      return;
    }
    setPricingSkuDebugBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch(`/api/v1/pricing-automation/sku-debug?sku=${encodeURIComponent(sku)}`);
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to debug SKU.');
      setPricingSkuDebug(data);
      setMessage(`Loaded SKU debug for ${sku}.`);
    } catch (e) {
      setError(e.message || 'Failed to debug SKU.');
    } finally {
      setPricingSkuDebugBusy(false);
    }
  }, [authedFetch, pricingTestSkus]);

  const backfillPricingSkuGids = useCallback(async () => {
    if (!pricingSkuDebug?.sku) return;
    const productIds = Array.from(
      new Set((pricingSkuDebug?.variants || []).map((v) => String(v?.productId || '').trim()).filter(Boolean))
    );
    if (productIds.length === 0) {
      setError('No product ids found for this SKU.');
      return;
    }
    setPricingGidBackfillBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch('/api/v1/shopify/backfill-product-gids', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to backfill product GIDs.');
      setMessage(data.message || 'Backfill completed.');
      // Reload the debug view so the user can immediately see GIDs filled.
      await debugPricingSku();
    } catch (e) {
      setError(e.message || 'Failed to backfill product GIDs.');
    } finally {
      setPricingGidBackfillBusy(false);
    }
  }, [authedFetch, debugPricingSku, pricingSkuDebug]);

  const backfillAllShopifyGids = useCallback(async () => {
    const confirmed = window.confirm('Backfill GIDs for ALL Shopify products now? This may take a few minutes.');
    if (!confirmed) return;
    setBackfillAllBusy(true);
    setError('');
    setMessage('');
    try {
      let cursor = null;
      let updatedTotal = 0;
      let hasNextPage = true;
      // Loop until done; each call processes a few pages to avoid timeouts.
      while (hasNextPage) {
        const resp = await authedFetch('/api/v1/shopify/backfill-all-gids', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cursor, maxPages: 3 }),
        });
        const data = await resp.json();
        if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed backfill all GIDs.');
        updatedTotal += Number(data.updatedProducts || 0);
        cursor = data.nextCursor == null ? null : data.nextCursor;
        hasNextPage = data.hasNextPage === true;
        setBackfillAllProgress({ updated: updatedTotal, cursor, hasNextPage });
        // small pause to keep UI responsive
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 250));
      }
      setMessage(`Backfill all GIDs complete. Updated products: ${updatedTotal}.`);
    } catch (e) {
      setError(e.message || 'Failed backfill all GIDs.');
    } finally {
      setBackfillAllBusy(false);
    }
  }, [authedFetch]);

  const calculatePriceAndLabels = useCallback(async () => {
    setComboBusy(true);
    setError('');
    setMessage('');
    try {
      const skuFilter = String(pricingTestSkus || '')
        .split(/[,\n]/g)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      const resp = await authedFetch('/api/v1/price-labels/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skuFilter }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to calculate price+labels.');

      const priceData = data.pricing || {};
      const labelData = data.labels || {};
      setPricingRun({
        runId: String(priceData.runId || ''),
        weekKey: String(priceData.weekKey || ''),
        proposedChanges: Number(priceData.proposedChanges || 0),
        counts: priceData.counts || {},
      });

      setMetafieldRun({
        runId: String(labelData.runId || ''),
        weekKey: String(labelData.weekKey || ''),
        proposedChanges: Number(labelData.proposedChanges || 0),
      });
      setMessage(`Calculated price + labels. Price proposed: ${Number(priceData.proposedChanges || 0)}. Labels proposed: ${Number(labelData?.proposedChanges || 0)}.`);
    } catch (e) {
      setError(e.message || 'Failed to calculate price + labels.');
    } finally {
      setComboBusy(false);
    }
  }, [authedFetch, pricingTestSkus]);

  const calculateStockPublish = useCallback(async ({ skuFilterOverride = null, fabricRulesOnly = false } = {}) => {
    setStockBusy(true);
    setError('');
    setMessage('');
    try {
      const skuFilter = Array.isArray(skuFilterOverride) ? skuFilterOverride : parseStockTestSkuFilter();
      const resp = await authedFetch('/api/v1/stock-publish/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skuFilter, fabricRulesOnly }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to calculate stock publish.');
      setStockRun({
        runId: String(data.runId || ''),
        proposedChanges: Number(data.proposedChanges || 0),
        summary: data.summary || {},
      });
      setMessage(`Calculated stock publish. Proposed updates: ${Number(data.proposedChanges || 0)}.`);
      loadLastStockRuns();
    } catch (e) {
      setError(e.message || 'Failed to calculate stock publish.');
    } finally {
      setStockBusy(false);
    }
  }, [authedFetch, loadLastStockRuns, parseStockTestSkuFilter]);

  const applyStockPublish = useCallback(async () => {
    const runId = String(stockRun?.runId || '').trim();
    if (!runId) {
      setError('Calculate stock publish first.');
      return;
    }
    const proposed = Number(stockRun?.proposedChanges || 0);
    const fabricMatches = Number(stockRun?.summary?.fabricRuleMatches || 0);
    const runKind = fabricMatches > 0 ? 'fabric-rule' : 'global product-type';
    const confirmed = window.confirm(`Apply selected ${runKind} stock run ${runId} to Shopify? Proposed updates: ${proposed}.`);
    if (!confirmed) return;
    if (fabricMatches <= 0 && proposed > 1000) {
      const largeRunConfirmed = window.confirm(`This is a large global product-type run with ${proposed} proposed updates. Fabric rule pushes should use "Push All Rules" in Fabric Stock Rules. Continue applying this global run?`);
      if (!largeRunConfirmed) return;
    }
    setStockBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch('/api/v1/stock-publish/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ runId }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to apply stock publish.');
      setMessage(data?.message || `Applied ${Number(data?.appliedChanges || 0)} stock updates.`);
      loadLastStockRuns();
    } catch (e) {
      setError(e.message || 'Failed to apply stock publish.');
    } finally {
      setStockBusy(false);
    }
  }, [authedFetch, loadLastStockRuns, stockRun?.proposedChanges, stockRun?.runId, stockRun?.summary?.fabricRuleMatches]);

  const downloadMyntraStockCsv = useCallback(async () => {
    if (!canUseMyntraStock) {
      setError('You do not have permission to use Myntra Stock.');
      return;
    }
    setMyntraStockBusy(true);
    setError('');
    setMessage('');
    try {
      const payload = {};
      let inputCount = null;
      if (myntraStockFile) {
        const skuList = await parseMyntraSkuCsvFile(myntraStockFile);
        payload.skuList = skuList;
        payload.sourceFileName = String(myntraStockFile.name || '').trim() || null;
        inputCount = skuList.length;
      } else {
        payload.useStored = true;
      }

      const resp = await authedFetch('/api/v1/stock-publish/myntra-stock.csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const msg = await parseErrorMessage(resp, 'Failed to generate Myntra stock CSV.');
        throw new Error(msg);
      }

      const blob = await resp.blob();
      const csvText = await blob.text();
      const lineCount = csvText
        .split(/\r?\n/g)
        .filter((line) => String(line || '').trim().length > 0)
        .length;
      const rowCount = Math.max(0, lineCount - 1);
      const contentDisposition = resp.headers.get('content-disposition');
      const filename = extractFilenameFromDisposition(contentDisposition, `myntra-stock-${new Date().toISOString().slice(0, 10)}.csv`);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      const sourceLabel = inputCount == null ? 'stored SKU list' : `uploaded CSV (${inputCount} SKU${inputCount === 1 ? '' : 's'})`;
      setMessage(`Downloaded Myntra stock CSV for ${rowCount} SKU${rowCount === 1 ? '' : 's'} using ${sourceLabel}.`);
      if (myntraStockFile) setMyntraStockFile(null);
    } catch (e) {
      setError(e.message || 'Failed to generate Myntra stock CSV.');
    } finally {
      setMyntraStockBusy(false);
    }
  }, [authedFetch, canUseMyntraStock, myntraStockFile]);

  const downloadLatestMyntraSkuCsv = useCallback(async () => {
    if (!canUseMyntraStock) {
      setError('You do not have permission to use Myntra Stock.');
      return;
    }
    setMyntraLatestSkuBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch('/api/v1/stock-publish/myntra-skus-latest.csv');
      if (!resp.ok) {
        const msg = await parseErrorMessage(resp, 'Failed to download latest Myntra SKU CSV.');
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const contentDisposition = resp.headers.get('content-disposition');
      const filename = extractFilenameFromDisposition(contentDisposition, `myntra-skus-latest-${new Date().toISOString().slice(0, 10)}.csv`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMessage('Downloaded latest uploaded Myntra SKU CSV.');
    } catch (e) {
      setError(e.message || 'Failed to download latest Myntra SKU CSV.');
    } finally {
      setMyntraLatestSkuBusy(false);
    }
  }, [authedFetch, canUseMyntraStock]);

  const applyPriceAndLabels = useCallback(async () => {
    const confirmed = window.confirm('Apply latest price + labels changes to Shopify?');
    if (!confirmed) return;
    setComboBusy(true);
    setError('');
    setMessage('');
    try {
      const skuFilter = String(pricingTestSkus || '')
        .split(/[,\n]/g)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      const resp = await authedFetch('/api/v1/price-labels/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skuFilter,
          pricingRunId: pricingRun.runId || '',
          labelsRunId: metafieldRun.runId || '',
        }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to apply price+labels.');
      const pMsg = data?.pricing?.message || '';
      const lMsg = data?.labels?.message || '';
      const prog = data?.progress || {};
      const p = prog?.pricing || {};
      const l = prog?.labels || {};
      const progressLine = (prog?.status === 'done')
        ? 'Done.'
        : `Progress: pricing ${Number(p.applied || 0)}/${Number(p.total || 0)}; labels ${Number(l.applied || 0)}/${Number(l.total || 0)}. Click Apply again to continue.`;
      setMessage(`Applied batch. ${pMsg} ${lMsg} ${progressLine}`.trim());
    } catch (e) {
      setError(e.message || 'Failed to apply price + labels.');
    } finally {
      setComboBusy(false);
    }
  }, [authedFetch, metafieldRun.runId, pricingRun.runId, pricingTestSkus]);

  const calculateWeightSync = useCallback(async () => {
    setWeightSyncBusy(true);
    setError('');
    setMessage('');
    try {
      const skuFilter = String(pricingTestSkus || '')
        .split(/[,\n]/g)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      const resp = await authedFetch('/api/v1/weight-sync/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skuFilter }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to calculate weight sync.');
      setWeightSyncPreview(data || null);
      setMessage(`Weight preview ready. Changed items: ${Number(data?.summary?.changedItems || 0)}.`);
    } catch (e) {
      setError(e.message || 'Failed to calculate weight sync.');
    } finally {
      setWeightSyncBusy(false);
    }
  }, [authedFetch, pricingTestSkus]);

  const applyWeightSync = useCallback(async () => {
    const changed = Number(weightSyncPreview?.summary?.changedItems || 0);
    if (changed <= 0) {
      setError('No weight changes to apply. Run Weight Preview first.');
      return;
    }
    const confirmed = window.confirm(`Apply weight updates now? Changed items: ${changed}. This updates only diff values.`);
    if (!confirmed) return;
    setWeightSyncBusy(true);
    setError('');
    setMessage('');
    try {
      const skuFilter = String(pricingTestSkus || '')
        .split(/[,\n]/g)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      const resp = await authedFetch('/api/v1/weight-sync/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skuFilter }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to apply weight sync.');
      setMessage(data?.message || `Weight sync applied. Updated ${Number(data?.summary?.applied || 0)} item(s).`);
      await calculateWeightSync();
    } catch (e) {
      setError(e.message || 'Failed to apply weight sync.');
    } finally {
      setWeightSyncBusy(false);
    }
  }, [authedFetch, calculateWeightSync, pricingTestSkus, weightSyncPreview?.summary?.changedItems]);

  // Individual apply/calc buttons are intentionally hidden from the UI.
  // We only expose the combined Price+Labels workflow.

  const loadMetafieldDiff = useCallback(async () => {
    const runId = String(metafieldRun.runId || '').trim();
    if (!runId) return;
    const skuFilter = String(pricingTestSkus || '')
      .split(/[,\n]/g)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    const sku = skuFilter.length ? skuFilter[0] : '';
    setMetafieldDiffBusy(true);
    setError('');
    try {
      const qs = new URLSearchParams({ runId });
      if (sku) qs.set('sku', sku);
      const resp = await authedFetch(`/api/v1/metafield-automation/diff?${qs.toString()}`);
      const data = await resp.json();
      if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to load labels diff.');
      setMetafieldDiff(Array.isArray(data.changes) ? data.changes : []);
    } catch (e) {
      setError(e.message || 'Failed to load labels diff.');
    } finally {
      setMetafieldDiffBusy(false);
    }
  }, [authedFetch, metafieldRun.runId, pricingTestSkus]);

  useEffect(() => {
    fetchProducts(currentPage, debouncedSearchQuery);
  }, [fetchProducts, currentPage, debouncedSearchQuery]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const deriveWeeklyRunPhase = (run) => {
    const pricingDone = run?.pricing?.done === true;
    const labelsDone = run?.labels?.done === true;
    const labelsRunId = String(run?.labels?.runId || '').trim();
    const status = String(run?.status || '').trim().toLowerCase();
    if (status === 'done' || status === 'done_with_errors') return 'Completed';
    if (!pricingDone) return 'Pricing in progress';
    if (pricingDone && !labelsRunId) return 'Waiting for Step 4';
    if (pricingDone && labelsRunId && !labelsDone) return 'Labels in progress';
    if (pricingDone && labelsDone) return 'Completed';
    return 'Running';
  };

  const renderNewProductsTab = () => (
    <div className="new-products-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>New Products</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={openSwatchPrompts}
            title="AI Photoshoot Prompt Settings"
            style={{ background: 'none', border: '1px solid #cbd5e0', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, color: '#4a5568' }}
          >
            &#9881;
          </button>
          {(Object.keys(pendingByBatch).length > 0 || hasPendingInAnyBatch) && (
            <button className="np-action-btn np-send-btn" onClick={openSendPhotoshootModal}>Send for Photoshoot</button>
          )}
          <button className="np-action-btn np-send-btn" onClick={() => setAddPanelOpen(true)}>+ Add New Product</button>
        </div>
      </div>
      {newProductsError && <div className="np-error">{newProductsError}</div>}

      {/* Recent batch pills */}
      {npBatchListLoading ? <p style={{ fontSize: '0.85rem', color: '#718096' }}>Loading batches...</p> : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
            {npBatchList.slice(0, 5).map(b => (
              <button
                key={b.batchNumber}
                onClick={() => { setNpBatchFilter(b.batchNumber); setNpPage(1); }}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: '0.82rem', fontWeight: npBatchFilter === b.batchNumber ? 700 : 500,
                  border: npBatchFilter === b.batchNumber ? '2px solid #4c51bf' : '1px solid #cbd5e0',
                  background: npBatchFilter === b.batchNumber ? '#ebf4ff' : '#fff',
                  color: npBatchFilter === b.batchNumber ? '#4c51bf' : '#4a5568',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {b.batchNumber} <span style={{ fontSize: '0.72rem', color: '#718096', marginLeft: 4 }}>({b.total})</span>
              </button>
            ))}
            {npBatchList.length > 5 && (
              <select
                value={npBatchList.slice(0, 5).some(b => b.batchNumber === npBatchFilter) ? '' : npBatchFilter}
                onChange={e => { if (e.target.value) { setNpBatchFilter(e.target.value); setNpPage(1); } }}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e0', fontSize: '0.82rem', color: '#4a5568', cursor: 'pointer' }}
              >
                <option value="">More batches...</option>
                {npBatchList.slice(5).map(b => (
                  <option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} ({b.total})</option>
                ))}
              </select>
            )}
          </div>
          <div className="np-filters" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <select value={npStatusFilter} onChange={e => { setNpStatusFilter(e.target.value); setNpPage(1); }}>
              <option value="">All Statuses</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {npBatchFilter && (
              <span style={{ fontSize: '0.82rem', color: '#718096' }}>
                {npPagination.total} product{npPagination.total !== 1 ? 's' : ''} in {npBatchFilter}
              </span>
            )}
            <button
              className="np-action-btn"
              onClick={openPrintLabelsModal}
              style={{ backgroundColor: '#2563eb', color: '#fff', marginLeft: 'auto', padding: '6px 16px', fontWeight: 600, fontSize: '0.82rem' }}
            >
              Print Batch Labels
            </button>
          </div>
        </>
      )}

      {!npBatchFilter && !npBatchListLoading ? (
        <p className="muted" style={{ textAlign: 'center', padding: '2rem 0' }}>Select a batch above to view products.</p>
      ) : newProductsLoading ? <p>Loading...</p> : (
        newProducts.length === 0 ? <p className="muted">No products found in this batch.</p> : (
          <>
            <table className="np-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Design</th>
                  <th>Status</th>
                  <th>Fabric Photo</th>
                  <th>Photoshoot</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {newProducts.map(p => (
                  <tr key={p.sku}>
                    <td className="np-sku-cell" onClick={() => openEditPanel(p)} style={{ cursor: 'pointer' }}>{p.sku}</td>
                    <td>{p.designNumber}</td>
                    <td>
                      <span className="np-status-badge" style={{ backgroundColor: STATUS_COLORS[p.status] || '#a0aec0' }}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                    </td>
                    <td>
                      {p.fabricPhotoUrl ? (
                        <img src={p.fabricPhotoUrl} alt="fabric" className="np-fabric-thumb" />
                      ) : <span className="muted">—</span>}
                    </td>
                    <td>{(p.photoshootImageCount || (p.photoshootImages || []).length) > 0 ? `${p.photoshootImageCount || p.photoshootImages.length} photo(s)` : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {p.status === 'photoshoot_sent' && (
                          <button className="np-action-btn np-recv-btn" disabled={npActionBusy === `recv-${p.sku}`} onClick={() => handleMarkReceived(p.sku)}>
                            {npActionBusy === `recv-${p.sku}` ? '...' : 'Mark Received'}
                          </button>
                        )}
                        {canUploadPhotoshoot && isUploadWindowOpen(p) && (
                          <button className="np-action-btn" style={{ backgroundColor: '#3182ce' }} onClick={() => setPhotoshootPanelProduct(p)}>Upload Photos</button>
                        )}
                        {isAdmin && canPushShopify && canRunShopifyPipeline(p) && (
                          <button
                            className="np-action-btn"
                            style={{ backgroundColor: p.status === 'ready_for_shopify' ? '#38a169' : '#2b6cb0' }}
                            disabled={npActionBusy === `shopify-${p.sku}`}
                            onClick={() => handleShopifyPipelineAction(p)}
                            title={p.pipelineMessage || ''}
                          >
                            {getShopifyPipelineButtonLabel(p)}
                          </button>
                        )}
                        {p.shopifyProductId && (
                          <span style={{ fontSize: '0.75rem', color: '#38a169' }}>Shopify: {p.shopifyProductId}</span>
                        )}
                        {canEdit && (
                          <button className="np-action-btn" style={{ backgroundColor: '#d69e2e' }} onClick={() => openEditPanel(p)}>Edit</button>
                        )}
                        {canDelete && (
                          <button className="np-action-btn" style={{ backgroundColor: '#e53e3e' }} disabled={npActionBusy === `del-${p.sku}`} onClick={() => handleDeleteProduct(p.sku)}>
                            {npActionBusy === `del-${p.sku}` ? '...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {npPagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16, paddingBottom: 8 }}>
                <button
                  onClick={() => setNpPage(p => Math.max(1, p - 1))}
                  disabled={npPage <= 1}
                  style={{ padding: '4px 12px', border: '1px solid #cbd5e0', borderRadius: 4, background: '#fff', cursor: npPage <= 1 ? 'default' : 'pointer', color: npPage <= 1 ? '#cbd5e0' : '#4a5568', fontSize: '0.85rem' }}
                >
                  Prev
                </button>
                {Array.from({ length: npPagination.totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === npPagination.totalPages || Math.abs(p - npPage) <= 2)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, i) =>
                    item === '...' ? (
                      <span key={`dot-${i}`} style={{ color: '#a0aec0', fontSize: '0.85rem' }}>...</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setNpPage(item)}
                        style={{
                          padding: '4px 10px', border: npPage === item ? '2px solid #4c51bf' : '1px solid #cbd5e0',
                          borderRadius: 4, background: npPage === item ? '#ebf4ff' : '#fff',
                          fontWeight: npPage === item ? 700 : 400, color: npPage === item ? '#4c51bf' : '#4a5568',
                          cursor: 'pointer', fontSize: '0.85rem',
                        }}
                      >
                        {item}
                      </button>
                    )
                  )}
                <button
                  onClick={() => setNpPage(p => Math.min(npPagination.totalPages, p + 1))}
                  disabled={npPage >= npPagination.totalPages}
                  style={{ padding: '4px 12px', border: '1px solid #cbd5e0', borderRadius: 4, background: '#fff', cursor: npPage >= npPagination.totalPages ? 'default' : 'pointer', color: npPage >= npPagination.totalPages ? '#cbd5e0' : '#4a5568', fontSize: '0.85rem' }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )
      )}
    </div>
  );

  const stockTestSkuFilter = parseStockTestSkuFilter();
  const stockSummary = stockRun?.summary || {};
  const stockProductTypeModeRows = useMemo(() => {
    const savedTypes = Object.keys(stockSettings.productTypeModes || {});
    return Array.from(new Set([
      ...stockProductTypes,
      ...savedTypes,
    ].map((type) => String(type || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [stockProductTypes, stockSettings.productTypeModes]);

  const updateStockProductTypeMode = useCallback((productType, mode) => {
    const normalizedType = String(productType || '').trim();
    if (!normalizedType) return;
    setStockSettings((prev) => {
      const nextModes = { ...(prev.productTypeModes || {}) };
      if (mode === 'default') {
        delete nextModes[normalizedType];
      } else {
        nextModes[normalizedType] = ['logic', 'logic_5', 'max_piece'].includes(mode) ? mode : 'actual';
      }
      return { ...prev, productTypeModes: nextModes };
    });
  }, []);

  const renderStockRuleStats = (summary, { compact = false } = {}) => {
    const stats = Array.isArray(summary?.fabricRuleStats) ? summary.fabricRuleStats : [];
    if (!stats.length) {
      return <div className="muted">No enabled Fabric Stock Rules were evaluated in this run.</div>;
    }
    return (
      <div className={`stock-rule-stats ${compact ? 'compact' : ''}`}>
        <table className="pricing-sku-debug-table">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Matched</th>
              <th>Changed</th>
              <th>Same</th>
              <th>Inventory</th>
              <th>Continue</th>
              <th>Publish</th>
              <th>Max Cart</th>
              <th>Skipped</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((stat, idx) => (
              <tr key={stat.ruleId || idx}>
                <td>
                  <div className="stock-rule-name">
                    <strong>{stat.fabricNameForOffice || 'Any fabric'}</strong>
                    <span>{stat.productType || 'Any type'} / {stat.quality || 'Any quality'}</span>
                  </div>
                </td>
                <td>{Number(stat.matchedSkus || 0)} SKU / {Number(stat.matchedVariants || 0)} var</td>
                <td className="stock-num changed">{Number(stat.changedDueToDifference ?? stat.changedVariants ?? 0)}</td>
                <td className="stock-num same">{Number(stat.notChangedSameNumbers ?? stat.unchangedVariants ?? 0)}</td>
                <td>{Number(stat.quantityChanges || 0)}</td>
                <td>{Number(stat.inventoryPolicyChanges || 0)}</td>
                <td>{Number(stat.publicationActions || 0)}</td>
                <td>{Number(stat.stockMetafieldChanges || 0)}</td>
                <td>{Number(stat.skippedMissingInventoryItemGid || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderStockPublishTab = () => (
    <div className="stock-publish-tab">
      <div className="stock-page-header">
        <div>
          <h1>Stock Publish</h1>
          <p className="muted">Controls Shopify stock, continue-selling, publish state, and max add-to-cart values from internal fabric inventory.</p>
        </div>
        <div className="stock-page-actions">
          <button type="button" className="secondary-btn" onClick={loadLastStockRuns} disabled={lastStockRunsBusy}>
            {lastStockRunsBusy ? 'Loading...' : 'Refresh Runs'}
          </button>
          <button type="button" className="primary-btn" onClick={saveStockSettings} disabled={stockBusy}>
            {stockBusy ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}
      {message && <p className="success-message">{message}</p>}

      <div className="stock-layout">
        <section className="stock-panel">
          <div className="stock-panel-title">
            <strong>Automation</strong>
            <span className="stock-status-pill on">Daily always on</span>
          </div>
          <div className="stock-settings-grid">
            <div className="stock-auto-note">
              <strong>Runs every day at 00:02 IST.</strong>
              <span>Only Fabric Stock Rules with the On checkbox enabled are processed. Turn individual rules off to pause them.</span>
            </div>
            <label>
              Location GID / ID
              <input
                type="text"
                value={stockSettings.locationGid || ''}
                onChange={(e) => setStockSettings((prev) => ({ ...prev, locationGid: e.target.value }))}
                placeholder="gid://shopify/Location/38778798125"
              />
            </label>
            <label>
              Default mode
              <select
                value={stockSettings.defaultMode || 'actual'}
                onChange={(e) => setStockSettings((prev) => ({
                  ...prev,
                  defaultMode: ['logic', 'logic_5', 'max_piece'].includes(e.target.value) ? e.target.value : 'actual',
                }))}
              >
                <option value="actual">Actual</option>
                <option value="logic">Logic</option>
                <option value="logic_5">Logic 5</option>
                <option value="max_piece">Max Piece</option>
              </select>
            </label>
          </div>
        </section>

        <section className="stock-panel">
          <div className="stock-panel-title"><strong>Product Type Modes</strong></div>
          <p className="muted">Optional overrides for stock quantity mode by Shopify product type. Types set to Default use the global default mode above.</p>
          {stockProductTypeModeRows.length ? (
            <div className="stock-product-type-modes">
              {stockProductTypeModeRows.map((productType) => {
                const savedMode = String((stockSettings.productTypeModes || {})[productType] || '').trim();
                return (
                  <label key={productType}>
                    <span>{productType}</span>
                    <select
                      value={savedMode || 'default'}
                      onChange={(e) => updateStockProductTypeMode(productType, e.target.value)}
                    >
                      <option value="default">Default</option>
                      <option value="actual">Actual</option>
                      <option value="logic">Logic</option>
                      <option value="logic_5">Logic 5</option>
                      <option value="max_piece">Max Piece</option>
                    </select>
                  </label>
                );
              })}
            </div>
          ) : (
            <p className="muted">No product types found yet.</p>
          )}
        </section>

        <section className="stock-panel">
          <div className="stock-panel-title"><strong>Test One Product</strong></div>
          <div className="stock-test-row">
            <label>
              Test SKU
              <input
                type="text"
                value={stockTestSkus}
                onChange={(e) => setStockTestSkus(e.target.value)}
                placeholder="POLI-CH-MA028067-110"
              />
            </label>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => calculateStockPublish({ skuFilterOverride: stockTestSkuFilter, fabricRulesOnly: true })}
              disabled={stockBusy || stockTestSkuFilter.length === 0}
            >
              {stockBusy ? 'Working...' : 'Calculate Test'}
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => calculateAndApplyStockRule('', stockTestSkuFilter)}
              disabled={stockBusy || !!stockFabricRuleBusy || stockTestSkuFilter.length === 0}
            >
              {stockFabricRuleBusy ? 'Working...' : 'Push Test'}
            </button>
          </div>
          <p className="muted">Enter the base SKU. Meter variants such as -2.5M and -3.5M are included automatically.</p>
        </section>
      </div>

      <section className="stock-panel">
        <div className="stock-panel-title"><strong>Current Preview</strong></div>
        <div className="stock-metric-grid">
          <div><span>Run</span><strong>{stockRun.runId || '—'}</strong></div>
          <div><span>Proposed</span><strong>{Number(stockRun.proposedChanges || 0)}</strong></div>
          <div><span>Same</span><strong>{Number(stockSummary.unchangedVariants || 0)}</strong></div>
          <div><span>Inventory</span><strong>{Number(stockSummary.quantityChanges || 0)}</strong></div>
          <div><span>Continue sell</span><strong>{Number(stockSummary.inventoryPolicyChanges || 0)}</strong></div>
          <div><span>Publish</span><strong>{Number(stockSummary.publicationActions || 0)}</strong></div>
          <div><span>Max cart</span><strong>{Number(stockSummary.stockMetafieldChanges || 0)}</strong></div>
        </div>
        <div className="stock-rule-stats-block">
          <div className="stock-panel-title compact-title"><strong>Rule Summary</strong></div>
          {renderStockRuleStats(stockSummary)}
        </div>
        <div className="stock-page-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => calculateStockPublish({ skuFilterOverride: [], fabricRulesOnly: true })}
            disabled={stockBusy}
          >
            {stockBusy ? 'Working...' : 'Calculate All Rules'}
          </button>
          <button type="button" className="primary-btn" onClick={applyStockPublish} disabled={stockBusy || !stockRun.runId}>
            {stockBusy ? 'Working...' : 'Apply Selected Run'}
          </button>
        </div>
      </section>

      <section className="stock-panel">
        <div className="stock-panel-title">
          <strong>Fabric Stock Rules</strong>
          <div className="stock-page-actions">
            <button type="button" className="secondary-btn" onClick={addStockFabricRule} disabled={stockBusy || !!stockFabricRuleBusy}>
              Add Rule
            </button>
            <button type="button" className="primary-btn" onClick={() => calculateAndApplyStockRule('', [])} disabled={stockBusy || !!stockFabricRuleBusy || !(stockSettings.fabricRules || []).length}>
              {stockFabricRuleBusy === 'all' ? 'Working...' : 'Push All Rules'}
            </button>
          </div>
        </div>
        <p className="muted">Rules match Fabric Specs by product type, quality, and Fabric Name for Office. Daily automation processes only rules that are checked On. Stock below 1m unpublishes; stock above 1m publishes back to selected channels.</p>
        <datalist id="stock-fabric-spec-options">
          {stockFabricSpecs.map((spec) => (
            <option
              key={spec.id}
              value={spec.id}
              label={`${spec.productType || '—'} · ${spec.quality || '—'} · ${spec.fabricNameForOffice || '—'}`}
            />
          ))}
        </datalist>
        <div className="stock-table-wrap">
          <table className="pricing-sku-debug-table">
            <thead>
              <tr>
                <th>On</th>
                <th>Spec</th>
                <th>Product Type</th>
                <th>Quality</th>
                <th>Fabric Office</th>
                <th>Qty Range</th>
                <th>Stock Mode</th>
                <th>Status</th>
                <th>Continue Sell</th>
                <th>Channels</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(stockSettings.fabricRules || []).map((rule) => (
                <tr key={rule.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={rule.enabled !== false}
                      onChange={(e) => updateStockFabricRule(rule.id, { enabled: e.target.checked })}
                    />
                  </td>
                  <td>
                    <input type="text" list="stock-fabric-spec-options" placeholder="Spec id" onBlur={(e) => applyStockFabricSpecToRule(rule.id, e.target.value)} />
                  </td>
                  <td>
                    <input type="text" value={rule.productType || ''} onChange={(e) => updateStockFabricRule(rule.id, { productType: e.target.value })} />
                  </td>
                  <td>
                    <input type="text" value={rule.quality || ''} onChange={(e) => updateStockFabricRule(rule.id, { quality: e.target.value })} />
                  </td>
                  <td>
                    <input type="text" value={rule.fabricNameForOffice || ''} onChange={(e) => updateStockFabricRule(rule.id, { fabricNameForOffice: e.target.value })} />
                  </td>
                  <td>
                    <div className="stock-inline-inputs">
                      <input type="number" value={rule.minQty ?? ''} onChange={(e) => updateStockFabricRule(rule.id, { minQty: e.target.value })} placeholder="Min" />
                      <input type="number" value={rule.maxQty ?? ''} onChange={(e) => updateStockFabricRule(rule.id, { maxQty: e.target.value })} placeholder="Max" />
                    </div>
                  </td>
                  <td>
                    <select value={rule.stockPublishMode || 'inherit'} onChange={(e) => updateStockFabricRule(rule.id, { stockPublishMode: e.target.value })}>
                      <option value="inherit">Inherit</option>
                      <option value="actual">Actual</option>
                      <option value="logic">Logic</option>
                      <option value="logic_5">Logic 5</option>
                      <option value="max_piece">Max Piece</option>
                    </select>
                  </td>
                  <td>
                    <select value={rule.productStatus || 'no_change'} onChange={(e) => updateStockFabricRule(rule.id, { productStatus: e.target.value })}>
                      <option value="no_change">No change</option>
                      <option value="active">Active</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </select>
                  </td>
                  <td>
                    <select value={rule.inventoryPolicy || 'no_change'} onChange={(e) => updateStockFabricRule(rule.id, { inventoryPolicy: e.target.value })}>
                      <option value="no_change">No change</option>
                      <option value="continue">Continue</option>
                      <option value="deny">Deny</option>
                    </select>
                  </td>
                  <td>
                    <select
                      multiple
                      value={Array.isArray(rule.publicationIds) ? rule.publicationIds : []}
                      onChange={(e) => updateStockFabricRule(rule.id, {
                        publicationIds: Array.from(e.target.selectedOptions).map((opt) => opt.value),
                      })}
                    >
                      {stockPublications.map((pub) => (
                        <option key={pub.id} value={pub.id}>{pub.name || pub.appTitle || pub.id}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="stock-row-actions">
                      <button type="button" className="secondary-btn" onClick={() => calculateAndApplyStockRule(rule.id, [])} disabled={stockBusy || !!stockFabricRuleBusy}>
                        {stockFabricRuleBusy === rule.id ? 'Working...' : 'Push Rule'}
                      </button>
                      <button type="button" className="secondary-btn" onClick={() => removeStockFabricRule(rule.id)} disabled={stockBusy || !!stockFabricRuleBusy}>
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!(stockSettings.fabricRules || []).length ? (
                <tr>
                  <td colSpan={11} className="muted">No fabric rules yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="stock-panel">
        <div className="stock-panel-title"><strong>Last Stock Runs</strong></div>
        {Array.isArray(lastStockRuns) && lastStockRuns.length ? (
          <div className="stock-table-wrap compact">
            <table className="pricing-sku-debug-table">
              <thead>
                <tr>
                  <th>Updated</th>
                  <th>Status</th>
                  <th>Proposed</th>
                  <th>Applied</th>
                  <th>Changed</th>
                  <th>Same</th>
                  <th>Error</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {lastStockRuns.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr>
                      <td className="mono">{r.updatedAt ? r.updatedAt.slice(0, 19).replace('T', ' ') : '—'}</td>
                      <td>{r.status || '—'}</td>
                      <td>{Number(r.proposedChanges || 0)}</td>
                      <td>{Number(r.appliedChanges || 0)}</td>
                      <td>{Number(r?.summary?.changedVariants || 0)}</td>
                      <td>{Number(r?.summary?.unchangedVariants || 0)}</td>
                      <td className="muted">{r.lastError || '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="secondary-btn"
                          disabled={!String(r?.runId || '').trim()}
                          onClick={() => {
                            const runId = String(r?.runId || '').trim();
                            if (!runId) return;
                            setStockRun((prev) => ({
                              ...prev,
                              runId,
                              proposedChanges: Number(r?.proposedChanges || 0),
                              summary: r?.summary || {},
                            }));
                            setMessage(`Loaded stock run ID ${runId}. You can now click Apply Selected Run to use this run.`);
                          }}
                        >
                          Use
                        </button>
                      </td>
                    </tr>
                    <tr className="stock-run-rule-row">
                      <td colSpan={8}>{renderStockRuleStats(r?.summary || {}, { compact: true })}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="muted">{lastStockRunsBusy ? 'Loading...' : 'No stock runs yet.'}</div>
        )}
      </section>
    </div>
  );

  return (
    <div className="products-container">
      <div className="products-tabs">
        <button className={`products-tab ${activeTab === 'catalog' ? 'active' : ''}`} onClick={() => setActiveTab('catalog')}>Catalog</button>
        <button className={`products-tab ${activeTab === 'newProducts' ? 'active' : ''}`} onClick={() => setActiveTab('newProducts')}>New Products</button>
        {isAdmin ? (
          <button className={`products-tab ${activeTab === 'stockPublish' ? 'active' : ''}`} onClick={() => setActiveTab('stockPublish')}>Stock Publish</button>
        ) : null}
      </div>

      {activeTab === 'newProducts' ? renderNewProductsTab() : null}
      {activeTab === 'stockPublish' && isAdmin ? renderStockPublishTab() : null}

      {activeTab === 'catalog' ? (
      <div className="catalog-tab">
      <div className="page-header">
        <h1>Product Catalog</h1>
        {canUseMyntraStock ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const nextFile = e.target?.files?.[0] || null;
                setMyntraStockFile(nextFile);
              }}
              style={{ maxWidth: 220 }}
            />
            <button type="button" className="primary-btn" onClick={downloadMyntraStockCsv} disabled={myntraStockBusy}>
              {myntraStockBusy ? 'Working...' : 'Myntra Stock'}
            </button>
            <button type="button" className="secondary-btn" onClick={downloadLatestMyntraSkuCsv} disabled={myntraLatestSkuBusy}>
              {myntraLatestSkuBusy ? 'Working...' : 'Latest SKU CSV'}
            </button>
          </div>
        ) : null}
        {isAdmin ? (
          <button
            type="button"
            className="pricing-settings-btn"
            title="Pricing automation"
            onClick={() => {
              setShowPricing(true);
              loadPricingSettings();
              loadStockSettings();
              loadStockFabricRuleOptions();
              loadLastPriceLabelRuns();
              loadLastStockRuns();
            }}
          >
            Pricing ⚙
          </button>
        ) : null}
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="Search by SKU or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="clear-search-btn">&times;</button>
          )}
        </div>
      </div>
      {canUseMyntraStock ? (
        <div className="muted" style={{ marginTop: -6, marginBottom: 8 }}>
          {myntraStockFile ? `Myntra CSV selected: ${myntraStockFile.name}` : 'No new Myntra CSV selected. Click Myntra Stock to use latest stored list.'}
        </div>
      ) : null}

      <div className="filters-bar">
        <div className="filter-group">
          <span className="filter-label">Include:</span>
          <label><input type="checkbox" checked={includeShopify} onChange={(e) => setIncludeShopify(e.target.checked)} /> Shopify</label>
          <label><input type="checkbox" checked={includeZoho} onChange={(e) => setIncludeZoho(e.target.checked)} /> Zoho</label>
          <label><input type="checkbox" checked={includeInventory} onChange={(e) => setIncludeInventory(e.target.checked)} /> Inventory</label>
        </div>
        <div className="filter-group">
          <span className="filter-label">Missing:</span>
          <label><input type="checkbox" checked={missingShopify} onChange={(e) => setMissingShopify(e.target.checked)} /> Not in Shopify</label>
          <label><input type="checkbox" checked={missingZoho} onChange={(e) => setMissingZoho(e.target.checked)} /> Not in Zoho</label>
          <label><input type="checkbox" checked={missingInventory} onChange={(e) => setMissingInventory(e.target.checked)} /> Not in Inventory</label>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}
      {message && <p className="success-message">{message}</p>}

      <div className="table-wrapper">
        {isLoading && <div className="loading-overlay"><p>Loading Products...</p></div>}
        <table className={`products-table ${isLoading ? 'loading' : ''}`}>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Master file number</th>
              <th>Sources</th>
              <th>Inventory (pieces)</th>
              <th>Inventory (m)</th>
              <th>Shopify Qty</th>
              <th>Zoho Qty</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.sku} className="product-row">
                <td className="sku-cell">
                  <div className="product-info-cell">
                    {product.displayImage ? (
                      <img src={product.displayImage} alt={product.displayTitle} className="product-thumbnail" />
                    ) : (
                      <div className="product-thumbnail-placeholder">No Image</div>
                    )}
                    <span>{product.sku}</span>
                  </div>
                </td>
                <td>
                  {designEditSku === product.sku ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input aria-label={`Master design file number for ${product.sku}`} value={designEditValue} onChange={e => setDesignEditValue(e.target.value.toUpperCase())} style={{ width: 115 }} />
                      <button type="button" disabled={designEditBusy} onClick={async () => { if (await saveDesignFile(product.sku, designEditValue)) setDesignEditSku(''); }}>Save</button>
                      <button type="button" disabled={designEditBusy} onClick={() => setDesignEditSku('')}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span>{product.masterDesignFileNumber || 'SKU default'}</span>
                      {canEdit && <button type="button" onClick={() => { setDesignEditSku(product.sku); setDesignEditValue(product.masterDesignFileNumber || ''); }}>Edit</button>}
                    </div>
                  )}
                </td>
                <td>
                  <div className="source-badges">
                    <span className={`source-tag ${product.inShopify ? 'present' : 'missing'}`}>{product.inShopify ? 'Shopify' : 'No Shopify'}</span>
                    <span className={`source-tag ${product.inZoho ? 'present' : 'missing'}`}>{product.inZoho ? 'Zoho' : 'No Zoho'}</span>
                    <span className={`source-tag ${product.inInventory ? 'present' : 'missing'}`}>{product.inInventory ? 'Inventory' : 'No Inventory'}</span>
                  </div>
                </td>
                <td>{product.inventoryPieceCount ? product.inventoryPieceCount : 0}</td>
                <td>{product.inventoryTotalLength ? product.inventoryTotalLength.toFixed(2) : '0.00'}</td>
                <td>{product.shopifyInventory !== undefined ? product.shopifyInventory : '—'}</td>
                <td>{product.zohoInventory !== undefined ? product.zohoInventory : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
      />

      {showPricing ? (
        <div className="pricing-modal-backdrop" onClick={() => setShowPricing(false)}>
          <div className="pricing-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Product Automation</h3>
            <p className="muted">Controls price, labels, and stock publishing. Price caps stay anchored to Fabric Specs sellPrice; stock publishing is a separate flow.</p>
            <div className="pricing-grid">
              <label>
                Test SKUs (comma/newline)
                <input
                  type="text"
                  value={pricingTestSkus}
                  onChange={(e) => setPricingTestSkus(e.target.value)}
                  placeholder="e.g. ABC-123, XYZ-999"
                />
              </label>
              <label>
                Charm price (end with 9)
                <select
                  value={pricingSettings.charmPriceEnabled !== false ? 'on' : 'off'}
                  onChange={(e) => setPricingSettings((p) => ({ ...p, charmPriceEnabled: e.target.value === 'on' }))}
                >
                  <option value="on">On</option>
                  <option value="off">Off</option>
                </select>
              </label>
              <label>
                Max uplift (%)
                <input type="number" step="0.01" value={pricingSettings.maxUpliftPct} onChange={(e) => setPricingSettings((p) => ({ ...p, maxUpliftPct: Number(e.target.value || 0) }))} />
              </label>
              <label>
                Max discount (%)
                <input type="number" step="0.01" value={pricingSettings.maxDiscountPct} onChange={(e) => setPricingSettings((p) => ({ ...p, maxDiscountPct: Number(e.target.value || 0) }))} />
              </label>
              <label>
                Step min (%)
                <input type="number" step="0.01" value={pricingSettings.stepPctMin} onChange={(e) => setPricingSettings((p) => ({ ...p, stepPctMin: Number(e.target.value || 0) }))} />
              </label>
              <label>
                Step max (%)
                <input type="number" step="0.01" value={pricingSettings.stepPctMax} onChange={(e) => setPricingSettings((p) => ({ ...p, stepPctMax: Number(e.target.value || 0) }))} />
              </label>
              <label>
                Top N uplift / group
                <input type="number" value={pricingSettings.topNPerGroup} onChange={(e) => setPricingSettings((p) => ({ ...p, topNPerGroup: Number(e.target.value || 0) }))} />
              </label>
              <label>
                Bottom K discount / group
                <input type="number" value={pricingSettings.bottomKPerGroup} onChange={(e) => setPricingSettings((p) => ({ ...p, bottomKPerGroup: Number(e.target.value || 0) }))} />
              </label>
              <label>
                Lookback (days)
                <input type="number" value={pricingSettings.lookbackDays} onChange={(e) => setPricingSettings((p) => ({ ...p, lookbackDays: Number(e.target.value || 0) }))} />
              </label>
              <label>
                High stock top (%)
                <input type="number" value={pricingSettings.highStockTopPercent} onChange={(e) => setPricingSettings((p) => ({ ...p, highStockTopPercent: Number(e.target.value || 0) }))} />
              </label>
              <label>
                Never discount product types (comma/newline)
                <textarea
                  rows={4}
                  value={Array.isArray(pricingSettings.neverDiscountProductTypes) ? pricingSettings.neverDiscountProductTypes.join('\n') : ''}
                  onChange={(e) => {
                    const list = String(e.target.value || '')
                      .split(/[\n,]/g)
                      .map((v) => String(v || '').trim())
                      .filter(Boolean);
                    setPricingSettings((p) => ({ ...p, neverDiscountProductTypes: list }));
                  }}
                  placeholder={'Example:\nKota Doria\nLinen'}
                />
              </label>
              <label>
                Force clear Label 0/1 for product types (comma/newline)
                <textarea
                  rows={4}
                  value={Array.isArray(pricingSettings.label01BlockedProductTypes) ? pricingSettings.label01BlockedProductTypes.join('\n') : ''}
                  onChange={(e) => {
                    const list = String(e.target.value || '')
                      .split(/[\n,]/g)
                      .map((v) => String(v || '').trim())
                      .filter(Boolean);
                    setPricingSettings((p) => ({ ...p, label01BlockedProductTypes: list }));
                  }}
                  placeholder={'Example:\nKota Doria\nLinen'}
                />
              </label>
              <label>
                Force clear Label 0-4 for product types (comma/newline)
                <textarea
                  rows={4}
                  value={Array.isArray(pricingSettings.labelAllBlockedProductTypes) ? pricingSettings.labelAllBlockedProductTypes.join('\n') : ''}
                  onChange={(e) => {
                    const list = String(e.target.value || '')
                      .split(/[\n,]/g)
                      .map((v) => String(v || '').trim())
                      .filter(Boolean);
                    setPricingSettings((p) => ({ ...p, labelAllBlockedProductTypes: list }));
                  }}
                  placeholder={'Example:\nKota Doria\nLinen'}
                />
              </label>
            </div>
            <div className="pricing-run-summary">
              <div><strong>Last calc</strong>: {pricingRun.runId ? `${pricingRun.runId} (${pricingRun.weekKey})` : '—'}</div>
              <div><strong>Proposed</strong>: {Number(pricingRun.proposedChanges || 0)}</div>
            </div>
            <div className="pricing-run-summary">
              <div><strong>Labels calc</strong>: {metafieldRun.runId ? `${metafieldRun.runId} (${metafieldRun.weekKey})` : '—'}</div>
              <div><strong>Proposed</strong>: {Number(metafieldRun.proposedChanges || 0)}</div>
            </div>
            {metafieldRun.runId ? (
              <div className="pricing-actions">
                <button type="button" className="secondary-btn" onClick={loadMetafieldDiff} disabled={comboBusy || pricingBusy || metafieldDiffBusy}>
                  {(comboBusy || pricingBusy || metafieldDiffBusy) ? 'Working...' : 'Price+Labels Diff'}
                </button>
              </div>
            ) : null}
            {Array.isArray(metafieldDiff) && metafieldDiff.length ? (
              <div className="pricing-sku-debug">
                <div className="pricing-sku-debug-title"><strong>Labels Diff</strong> (showing {metafieldDiff.length})</div>
                <div className="pricing-sku-debug-tablewrap">
                  <table className="pricing-sku-debug-table">
                    <thead>
                      <tr>
                        <th>Product GID</th>
                        <th>Namespace</th>
                        <th>Key</th>
                        <th>New value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metafieldDiff.map((r, idx) => (
                        <tr key={`${r.product_gid || idx}-${r.namespace}-${r.key}`}>
                          <td className="mono">{r.product_gid || '—'}</td>
                          <td>{r.namespace}</td>
                          <td>{r.key}</td>
                          <td>{r.new_value === '' ? '—' : r.new_value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
            {pricingSkuDebug ? (
              <div className="pricing-sku-debug">
                <div className="pricing-sku-debug-title"><strong>SKU Debug</strong>: {pricingSkuDebug.sku}</div>
                <div className="pricing-sku-debug-grid">
                  <div><strong>Spec sell</strong>: {pricingSkuDebug?.spec?.sellPrice}</div>
                  <div><strong>Cap</strong>: {pricingSkuDebug?.spec?.capUpliftPrice}</div>
                  <div><strong>Effective</strong>: {pricingSkuDebug?.pricingState?.effectivePrice}</div>
                  <div><strong>Stored max price</strong>: {pricingSkuDebug?.shopifyCatalog?.maxStoredVariantPrice}</div>
                  <div><strong>Matched variants</strong>: {pricingSkuDebug?.shopifyCatalog?.matchedVariants}</div>
                  <div><strong>Missing GIDs</strong>: {pricingSkuDebug?.shopifyCatalog?.missingGidCount}</div>
                  <div><strong>Resolved from</strong>: {pricingSkuDebug?.spec?.resolvedFrom || '—'}</div>
                  <div><strong>Hard cap</strong>: {pricingSkuDebug?.settings?.enforceHardCap ? 'On' : 'Off'}</div>
                </div>
                {Number(pricingSkuDebug?.shopifyCatalog?.missingGidCount || 0) > 0 ? (
                  <div className="pricing-sku-debug-actions">
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={backfillPricingSkuGids}
                      disabled={pricingBusy || pricingSkuDebugBusy || pricingGidBackfillBusy}
                      title="Fetch these Shopify products and update Firestore with product_gid/variant_gid"
                    >
                      {(pricingBusy || pricingSkuDebugBusy || pricingGidBackfillBusy) ? 'Working...' : 'Backfill GIDs'}
                    </button>
                    <span className="muted">Needed before Apply can update Shopify for this SKU.</span>
                  </div>
                ) : null}
                {Array.isArray(pricingSkuDebug.variants) && pricingSkuDebug.variants.length ? (
                  <div className="pricing-sku-debug-tablewrap">
                    <table className="pricing-sku-debug-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Type</th>
                          <th>Price</th>
                          <th>Compare at</th>
                          <th>Has GIDs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingSkuDebug.variants.map((v, idx) => (
                          <tr key={`${v.productId}-${v.variantGid || idx}`}>
                            <td className="mono">{v.productId}</td>
                            <td>{v.productType || '—'}</td>
                            <td>{v.price}</td>
                            <td>{v.compareAtPrice == null ? '—' : v.compareAtPrice}</td>
                            <td>{v.productGid && v.variantGid ? 'Yes' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="muted">No matching variants found in Firestore shopify_products for this SKU.</div>
                )}
              </div>
            ) : null}
            <div className="pricing-actions">
              <button
                type="button"
                className="secondary-btn"
                onClick={backfillAllShopifyGids}
                disabled={pricingBusy || pricingSkuDebugBusy || pricingGidBackfillBusy || backfillAllBusy}
                title="Fetch all Shopify products and store product_gid/variant_gid in Firestore"
              >
                {(pricingBusy || pricingSkuDebugBusy || pricingGidBackfillBusy || backfillAllBusy) ? 'Working...' : 'Backfill All GIDs'}
              </button>
              <span className="muted">
                {backfillAllBusy
                  ? `Progress: updated ${backfillAllProgress.updated} products...`
                  : ''}
              </span>
            </div>
            <div className="pricing-actions">
              <button type="button" className="secondary-btn" onClick={() => setShowPricing(false)} disabled={pricingBusy}>Close</button>
              <button type="button" className="secondary-btn" onClick={savePricingSettings} disabled={pricingBusy}>{pricingBusy ? 'Working...' : 'Save'}</button>
              <button type="button" className="secondary-btn" onClick={debugPricingSku} disabled={pricingBusy || pricingSkuDebugBusy}>{(pricingBusy || pricingSkuDebugBusy) ? 'Working...' : 'Check SKU'}</button>
              <button type="button" className="secondary-btn" onClick={calculatePriceAndLabels} disabled={comboBusy || pricingBusy}>
                {(comboBusy || pricingBusy) ? 'Working...' : 'Calc Price+Labels'}
              </button>
              <button type="button" className="primary-btn" onClick={applyPriceAndLabels} disabled={comboBusy || !pricingRun.runId || !metafieldRun.runId}>
                {comboBusy ? 'Working...' : 'Apply Price+Labels'}
              </button>
            </div>
            <div className="muted" style={{ marginTop: 6 }}>
              Active run IDs: pricing={pricingRun.runId ? <span className="mono">{pricingRun.runId}</span> : '—'}; labels={metafieldRun.runId ? <span className="mono">{metafieldRun.runId}</span> : '—'}
            </div>

            {isAdmin ? (
              <div className="pricing-sku-debug">
                <div className="pricing-sku-debug-title">
                  <strong>Last Weekly Runs</strong>
                  <button type="button" className="secondary-btn" style={{ marginLeft: 10 }} onClick={loadLastPriceLabelRuns} disabled={lastPriceLabelRunsBusy}>
                    {lastPriceLabelRunsBusy ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
                {Array.isArray(lastPriceLabelRuns) && lastPriceLabelRuns.length ? (
                  <div className="pricing-sku-debug-tablewrap">
                    <table className="pricing-sku-debug-table">
                      <thead>
                        <tr>
                          <th>Updated</th>
                          <th>Phase</th>
                          <th>Status</th>
                          <th>Pricing</th>
                          <th>Labels</th>
                          <th>Error</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lastPriceLabelRuns.map((r) => (
                          <tr key={r.id}>
                            <td className="mono">{r.updatedAt ? r.updatedAt.slice(0, 19).replace('T', ' ') : '—'}</td>
                            <td>{deriveWeeklyRunPhase(r)}</td>
                            <td>{r.status || '—'}</td>
                            <td>
                              {Number(r?.pricing?.applied || 0)}/{Number(r?.pricing?.total || 0)}
                              {r?.pricing?.done ? ' (done)' : ''}
                              {Number(r?.pricing?.failed || 0) > 0 ? ` (failed ${Number(r?.pricing?.failed || 0)})` : ''}
                            </td>
                            <td>
                              {Number(r?.labels?.applied || 0)}/{Number(r?.labels?.total || 0)}
                              {!String(r?.labels?.runId || '').trim() && r?.pricing?.done ? ' (waiting)' : ''}
                              {r?.labels?.done ? ' (done)' : ''}
                              {Number(r?.labels?.failed || 0) > 0 ? ` (failed ${Number(r?.labels?.failed || 0)})` : ''}
                              {Number(r?.labels?.failedSkuCount || 0) > 0 ? ` (failed SKU ${Number(r?.labels?.failedSkuCount || 0)})` : ''}
                            </td>
                            <td>{r.lastError ? String(r.lastError).slice(0, 160) : '—'}</td>
                            <td>
                              <button
                                type="button"
                                className="secondary-btn"
                                disabled={!String(r?.pricing?.runId || '').trim() || !String(r?.labels?.runId || '').trim()}
                                onClick={() => {
                                  const p = String(r?.pricing?.runId || '').trim();
                                  const l = String(r?.labels?.runId || '').trim();
                                  if (!p || !l) return;
                                  setPricingRun((prev) => ({ ...prev, runId: p }));
                                  setMetafieldRun((prev) => ({ ...prev, runId: l }));
                                  setMessage(`Loaded run IDs. Pricing: ${p}. Labels: ${l}. You can now click Apply Price+Labels to resume.`);
                                }}
                                title="Load this run IDs into the active selection so you can resume Apply"
                              >
                                Use
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="muted">{lastPriceLabelRunsBusy ? 'Loading...' : 'No runs yet.'}</div>
                )}
              </div>
            ) : null}

            <div className="pricing-sku-debug">
              <div className="pricing-sku-debug-title"><strong>Weight Sync (Manual)</strong></div>
              <p className="muted">Uses Fabric Specs weight. Updates only rows where Shopify weight differs from fixed grams. Dynamic specs are skipped.</p>
              <div className="pricing-actions">
                <button type="button" className="secondary-btn" onClick={calculateWeightSync} disabled={weightSyncBusy}>
                  {weightSyncBusy ? 'Working...' : 'Weight Preview'}
                </button>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={applyWeightSync}
                  disabled={weightSyncBusy || Number(weightSyncPreview?.summary?.changedItems || 0) <= 0}
                >
                  {weightSyncBusy ? 'Working...' : 'Apply Weight Sync'}
                </button>
              </div>
              {weightSyncPreview?.summary ? (
                <div className="pricing-run-summary">
                  <div><strong>Scanned SKUs</strong>: {Number(weightSyncPreview.summary.scannedSkus || 0)}</div>
                  <div><strong>Changed items</strong>: {Number(weightSyncPreview.summary.changedItems || 0)}</div>
                  <div><strong>Skipped dynamic</strong>: {Number(weightSyncPreview.summary?.skipped?.dynamic || 0)}</div>
                  <div><strong>Skipped no spec</strong>: {Number(weightSyncPreview.summary?.skipped?.noSpec || 0)}</div>
                </div>
              ) : null}
              {Array.isArray(weightSyncPreview?.sample) && weightSyncPreview.sample.length ? (
                <div className="pricing-sku-debug-tablewrap">
                  <table className="pricing-sku-debug-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Current (g)</th>
                        <th>Target (g)</th>
                        <th>Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weightSyncPreview.sample.slice(0, 30).map((r, idx) => (
                        <tr key={`${r.sku}-${r.inventoryItemGid || idx}`}>
                          <td>{r.sku}</td>
                          <td>{r.currentGrams == null ? '—' : r.currentGrams}</td>
                          <td>{r.targetGrams}</td>
                          <td>{r.deltaGrams}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>

          </div>
        </div>
      ) : null}
      </div>
      ) : null}

      <AddProductPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        onSuccess={() => { setAddPanelOpen(false); fetchNpBatches(); fetchNewProducts(npPage); }}
        authedFetch={authedFetch}
        printSticker={printSticker}
      />

      {editPanelProduct && (
        <>
          <div className="ap-overlay" onClick={() => setEditPanelProduct(null)} />
          <div className="ap-panel">
            <div className="ap-panel-header">
              <h2>Edit {editPanelProduct.sku}</h2>
              <button className="ap-close-btn" onClick={() => setEditPanelProduct(null)}>&times;</button>
            </div>
            <div className="ap-panel-body">
              {/* Product Info / Details Section */}
              <div className="ap-section" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '1.25rem', marginBottom: '1.25rem' }}>
                <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#2d3748', fontWeight: 600 }}>Product Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', fontSize: '0.85rem', color: '#4a5568' }}>
                  <div><strong>Design Number:</strong> {editPanelProduct.designNumber}</div>
                  <div><strong>Status:</strong> <span className="np-status-badge" style={{ backgroundColor: STATUS_COLORS[editPanelProduct.status] || '#a0aec0', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12 }}>{STATUS_LABELS[editPanelProduct.status] || editPanelProduct.status}</span></div>
                  <div><strong>Sell Price:</strong> ₹{editPanelProduct.sellPrice || '0'}</div>
                  <div><strong>Purchase Price:</strong> ₹{editPanelProduct.purchasePrice || '0'}</div>
                  <div style={{ gridColumn: 'span 2' }}><strong>Piece IDs:</strong> {(editPanelProduct.pieceIds || []).join(', ') || '—'}</div>
                </div>
                {(() => {
                  const reviewMap = editPanelProduct.aiPhotoshootReview || {};
                  const productStyleKeys = [...new Set((editPanelProduct.photoshootImages || []).map(img => String(img.style || '').toUpperCase()).filter(Boolean))];
                  if (productStyleKeys.length === 0) return null;
                  const allReviewed = productStyleKeys.every(key => ['approved', 'skipped'].includes(reviewMap[key]));
                  if (!allReviewed) return null;
                  const approvedCount = productStyleKeys.filter(key => reviewMap[key] === 'approved').length;
                  const skippedCount = productStyleKeys.filter(key => reviewMap[key] === 'skipped').length;
                  return (
                    <div style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 6, background: '#f0fff4', border: '1px solid #9ae6b4', color: '#276749', fontSize: '0.8rem', fontWeight: 700 }}>
                      <span>Ready for Shopify</span>
                      <span style={{ color: '#2f855a', fontWeight: 600 }}>{approvedCount} approved · {skippedCount} skipped</span>
                    </div>
                  );
                })()}
              </div>

              {/* Editable Fields Section */}
              <div className="ap-section">
                <label className="ap-label" htmlFor="master-design-file-number">Master design file number</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input id="master-design-file-number" type="text" value={newProductDesignFile} onChange={e => setNewProductDesignFile(e.target.value.toUpperCase())} placeholder="Use SKU design number" />
                  <button type="button" disabled={designEditBusy || !canEdit} onClick={() => saveDesignFile(editPanelProduct.sku, newProductDesignFile)}>Save</button>
                </div>
              </div>
              <div className="ap-section">
                <label className="ap-label">Batch Number</label>
                <input type="text" value={editBatchNumber} onChange={e => setEditBatchNumber(e.target.value)} />
              </div>
              <div className="ap-section">
                <label className="ap-label">Fabric Reference Photos (Upload 3-6 photos)</label>
                <input type="file" accept="image/*" multiple disabled={!!photoUploadProgress} onChange={e => {
                  const files = Array.from(e.target.files);
                  if (!files.length) return;
                  const currentCount = editFabricPhotos.length;
                  if (currentCount + files.length > 6) {
                    alert(`You can upload a maximum of 6 reference photos. ${currentCount} already uploaded.`);
                    e.target.value = '';
                    return;
                  }
                  e.target.value = '';
                  autoUploadPhotos(files, 'fabric');
                }} />
                <p className="ap-photo-limit-text">Upload front, back, scale, drape etc. for accurate AI generation. Photos auto-save on upload.</p>
                {photoUploadProgress && (
                  <div style={{ margin: '0.5rem 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#4a5568', marginBottom: 4 }}>
                      <span>{photoUploadProgress.label}</span>
                      <span>{Math.round((photoUploadProgress.current / photoUploadProgress.total) * 100)}%</span>
                    </div>
                    <div style={{ background: '#e2e8f0', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                      <div style={{ background: '#4299e1', height: '100%', borderRadius: 4, transition: 'width 0.3s ease', width: `${(photoUploadProgress.current / photoUploadProgress.total) * 100}%` }} />
                    </div>
                  </div>
                )}
                {editFabricPhotos.length > 0 && (
                  <div className="ap-photos-grid">
                    {editFabricPhotos.map((photo, index) => (
                      <div key={index} className="ap-photo-container">
                        <img src={photo.preview} alt={`Fabric Swatch ${index + 1}`} className="ap-photo-thumbnail" />
                        {photo.url && (
                          <button type="button" className="ap-photo-download-btn" title="Download photo" onClick={() => downloadPhoto(photo.url, `${editPanelProduct?.sku || 'fabric'}-ref-${index + 1}.jpg`)}>&#8681;</button>
                        )}
                        <button type="button" className="ap-photo-remove-btn" disabled={!!photoUploadProgress} onClick={() => handleRemoveFabricPhoto(index)} title="Remove photo">&times;</button>
                      </div>
                    ))}
                  </div>
                )}

                <label className="ap-label">Scale Reference Photo (with measuring tape)</label>
                <input type="file" accept="image/*" disabled={!!photoUploadProgress} onChange={e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = '';
                  autoUploadPhotos([file], 'scale');
                }} />
                <p className="ap-photo-limit-text">Upload one photo where the measuring tape/ruler is visible on the fabric. AI uses this for motif-size accuracy.</p>
                {editScaleReferencePhoto && (
                  <div className="ap-photos-grid">
                    <div className="ap-photo-container">
                      <img src={editScaleReferencePhoto.preview} alt="Scale reference" className="ap-photo-thumbnail" />
                      {editScaleReferencePhoto.url && (
                        <button type="button" className="ap-photo-download-btn" title="Download photo" onClick={() => downloadPhoto(editScaleReferencePhoto.url, `${editPanelProduct?.sku || 'fabric'}-scale-ref.jpg`)}>&#8681;</button>
                      )}
                      <button type="button" className="ap-photo-remove-btn" disabled={!!photoUploadProgress} onClick={handleRemoveScaleRef} title="Remove scale reference">&times;</button>
                    </div>
                  </div>
                )}

                <label className="ap-label">Sheer / Transparency Reference Photo</label>
                <input type="file" accept="image/*" disabled={!!photoUploadProgress} onChange={e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = '';
                  autoUploadPhotos([file], 'sheer');
                }} />
                <p className="ap-photo-limit-text">Upload one photo with a hand or dark card behind a single fabric layer. AI uses this for transparency accuracy.</p>
                {editSheerReferencePhoto && (
                  <div className="ap-photos-grid">
                    <div className="ap-photo-container">
                      <img src={editSheerReferencePhoto.preview} alt="Sheer reference" className="ap-photo-thumbnail" />
                      {editSheerReferencePhoto.url && (
                        <button type="button" className="ap-photo-download-btn" title="Download photo" onClick={() => downloadPhoto(editSheerReferencePhoto.url, `${editPanelProduct?.sku || 'fabric'}-sheer-ref.jpg`)}>&#8681;</button>
                      )}
                      <button type="button" className="ap-photo-remove-btn" disabled={!!photoUploadProgress} onClick={handleRemoveSheerRef} title="Remove sheer reference">&times;</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Photoshoot Images Section if present */}
              {(editPanelProduct.photoshootImages || []).length > 0 && (
                <div className="ap-section" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
                  <label className="ap-label">Photoshoot Images ({(editPanelProduct.photoshootImages || []).length})</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                    {editPanelProduct.photoshootImages.map((img, i) => {
                      const styleKey = String(img.style || '').toUpperCase();
                      const reviewStatus = (editPanelProduct.aiPhotoshootReview || {})[styleKey] || img.reviewStatus || 'pending';
                      return (
                        <div key={`${styleKey || 'shoot'}-${i}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 92 }}>
                          <img
                            src={img.url}
                            alt={`shoot-${i}`}
                            title={`Click to open ${img.style || 'photoshoot'} image in new tab`}
                            onClick={() => window.open(img.url, '_blank')}
                            style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', cursor: 'pointer' }}
                          />
                          <span style={{ display: 'block', fontSize: '0.65rem', color: '#718096', textAlign: 'center', maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {img.style ? img.style.replace('_', ' ') : `shoot-${i}`}
                          </span>
                          <span style={{ fontSize: '0.62rem', color: reviewStatus === 'approved' ? '#2f855a' : reviewStatus === 'skipped' ? '#718096' : '#d69e2e', fontWeight: 700, textTransform: 'uppercase' }}>
                            {reviewStatus}
                          </span>
                          {styleKey && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, width: '100%' }}>
                              {reviewStatus === 'approved' ? (
                                <span style={{ fontSize: '0.62rem', padding: '3px 4px', border: '1px solid #c6f6d5', borderRadius: 4, background: '#f0fff4', color: '#2f855a', textAlign: 'center', fontWeight: 700 }}>Approved</span>
                              ) : canEdit ? (
                                <button type="button" disabled={aiStyleActionBusy} onClick={() => handleReviewInhouseAiStyle(styleKey, 'approve')} style={{ fontSize: '0.62rem', padding: '3px 4px', border: '1px solid #c6f6d5', borderRadius: 4, background: '#f0fff4', color: '#2f855a', cursor: aiStyleActionBusy ? 'wait' : 'pointer' }}>Approve</button>
                              ) : null}
                              {reviewStatus === 'skipped' ? (
                                <span style={{ fontSize: '0.62rem', padding: '3px 4px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#f7fafc', color: '#4a5568', textAlign: 'center', fontWeight: 700 }}>Skipped</span>
                              ) : canEdit ? (
                                <button type="button" disabled={aiStyleActionBusy} onClick={() => handleReviewInhouseAiStyle(styleKey, 'skip')} style={{ fontSize: '0.62rem', padding: '3px 4px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#f7fafc', color: '#4a5568', cursor: aiStyleActionBusy ? 'wait' : 'pointer' }}>Skip</button>
                              ) : null}
                              {(() => {
                                const regenCount = (editPanelProduct.regenerateCounts || {})[styleKey] || 0;
                                const regenLimitReached = regenCount >= 3;
                                const canRegen = canUploadPhotoshoot && (!regenLimitReached || isAdmin);
                                return (<>
                                  {canRegen && (
                                    <button type="button" disabled={aiStyleActionBusy} onClick={() => handleRegenerateInhouseAiStyle(styleKey)} style={{ gridColumn: 'span 2', fontSize: '0.62rem', padding: '3px 4px', border: '1px solid #bee3f8', borderRadius: 4, background: '#ebf8ff', color: '#2b6cb0', cursor: 'pointer' }}>
                                      {aiStyleActionBusy === `regen-${styleKey}` ? 'Regenerating...' : `Regenerate${regenCount > 0 ? ` (${regenCount}/3)` : ''}`}
                                    </button>
                                  )}
                                  {regenLimitReached && !isAdmin && canUploadPhotoshoot && (
                                    <span style={{ gridColumn: 'span 2', fontSize: '0.58rem', color: '#a0aec0', textAlign: 'center' }}>Regenerate limit reached</span>
                                  )}
                                </>);
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(() => {
                const reviewMap = editPanelProduct.aiPhotoshootReview || {};
                const pStyleKeys = [...new Set((editPanelProduct.photoshootImages || []).map(img => String(img.style || '').toUpperCase()).filter(Boolean))];
                const photoshootReviewed = pStyleKeys.length > 0 && pStyleKeys.every(key => ['approved', 'skipped'].includes(reviewMap[key]));
                if (!photoshootReviewed) return null;
                const detail = newProductWorkflow?.productDetails || null;
                const details = detail?.details || null;
                const shopify = details?.shopify || {};
                const detailGenerated = (detail?.generationStatus || detail?.status) === 'generated';
                const lookbook = newProductWorkflow?.lookbook || null;
                const generation = lookbook?.generation || {};
                const eligibleCodes = Array.isArray(lookbook?.eligibleOutfitCodes) ? lookbook.eligibleOutfitCodes : [];
                const readiness = newProductWorkflow?.readiness || null;
                return (
                  <>
                    <div className="ap-section" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
                      <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#2d3748', fontWeight: 600 }}>Product Details AI</h3>
                      <p style={{ fontSize: '0.8rem', color: '#718096', margin: '0 0 0.75rem' }}>
                        Generate title, description, SEO, tags, and fabric metadata before Lookbook generation.
                      </p>
                      {!detailGenerated ? (
                        <button type="button" className="ap-submit-btn" disabled={workflowBusy === 'details-generate'} onClick={handleGenerateProductDetails}>
                          {workflowBusy === 'details-generate' ? 'Generating Product Details...' : 'Generate Product Details'}
                        </button>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <details open style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 8 }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#2d3748' }}>Shopify Title / SEO / Tags</summary>
                            <div style={{ fontSize: '0.78rem', color: '#4a5568', marginTop: 8, display: 'grid', gap: 6 }}>
                              <div><strong>Title:</strong> {shopify.product_title || '—'}</div>
                              <div><strong>SEO Title:</strong> {shopify.seo_title || '—'}</div>
                              <div><strong>SEO Description:</strong> {shopify.seo_description || '—'}</div>
                              <div><strong>Tags:</strong> {Array.isArray(shopify.tags) ? shopify.tags.join(', ') : (shopify.tags || '—')}</div>
                            </div>
                          </details>
                          <details style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 8 }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#2d3748' }}>HTML Description</summary>
                            <div style={{ fontSize: '0.78rem', color: '#4a5568', marginTop: 8, maxHeight: 220, overflow: 'auto', border: '1px solid #edf2f7', borderRadius: 4, padding: 8 }} dangerouslySetInnerHTML={{ __html: shopify.html_description || '<span>—</span>' }} />
                          </details>
                          <details style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 8 }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#2d3748' }}>Attributes / Fabric Story</summary>
                            <pre style={{ fontSize: '0.72rem', whiteSpace: 'pre-wrap', background: '#f7fafc', padding: 8, borderRadius: 4, maxHeight: 260, overflow: 'auto' }}>
                              {JSON.stringify({
                                colors: details?.colors,
                                visual: details?.visual,
                                usage: details?.usage,
                                fabric_story: details?.fabric_story,
                                variant_fabric_stories: details?.variant_fabric_stories,
                              }, null, 2)}
                            </pre>
                          </details>
                          <div style={{ padding: '7px 10px', borderRadius: 6, background: '#f0fff4', border: '1px solid #9ae6b4', color: '#276749', fontSize: '0.8rem', fontWeight: 700 }}>
                            Product details generated and saved to Shopify draft
                          </div>
                        </div>
                      )}
                    </div>

                    {detailGenerated && (
                      <div className="ap-section" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#2d3748', fontWeight: 600 }}>Lookbook V2 Images</h3>
                        <p style={{ fontSize: '0.8rem', color: '#718096', margin: '0 0 0.75rem' }}>
                          Generate and approve customer-facing lookbook images for this product.
                        </p>
                        {!lookbook ? (
                          <div style={{ fontSize: '0.8rem', color: '#718096', padding: 10, background: '#f7fafc', borderRadius: 6 }}>
                            Lookbook setup will appear after Shopify draft and details are available.
                          </div>
                        ) : (
                          <>
                          {canGenerateLookbook && (() => {
                            const pendingCount = eligibleCodes.filter(code => (generation[code]?.status || 'pending') === 'pending').length;
                            return pendingCount > 0 ? (
                              <button type="button" disabled={lookbookGenerating} onClick={handleGenerateLookbook} style={{ width: '100%', marginBottom: 10, padding: '8px 12px', fontSize: '0.82rem', fontWeight: 700, border: '1px solid #9f7aea', borderRadius: 6, background: lookbookGenerating ? '#e9d8fd' : '#faf5ff', color: '#6b46c1', cursor: lookbookGenerating ? 'wait' : 'pointer' }}>
                                {lookbookGenerating ? `Generating ${pendingCount} lookbook image(s)...` : `Generate All Lookbook Images (${pendingCount} pending)`}
                              </button>
                            ) : null;
                          })()}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                            {eligibleCodes.map(code => {
                              const item = generation[code] || {};
                              const status = item.status || 'pending';
                              const approved = item.approvalStatus === 'approved' || item.publishStatus === 'queued' || item.publishStatus === 'published';
                              const rejected = item.approvalStatus === 'rejected';
                              const busyKey = workflowBusy.includes(code);
                              const imageUrl = item.imageUrl || item.masterImageUrl || item.shopifyImageUrl || '';
                              return (
                                <div key={code} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, background: '#fff' }}>
                                  {imageUrl ? (
                                    <img src={imageUrl} alt={code} onClick={() => window.open(imageUrl, '_blank')} style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 5, cursor: 'pointer' }} />
                                  ) : (
                                    <div style={{ width: '100%', aspectRatio: '1 / 1', borderRadius: 5, background: '#f7fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0aec0', fontSize: '0.75rem' }}>No image</div>
                                  )}
                                  <div style={{ marginTop: 6, fontSize: '0.75rem', fontWeight: 700, color: '#4a5568', textAlign: 'center' }}>{LOOKBOOK_V2_LABELS[code] || code}</div>
                                  <div style={{ fontSize: '0.68rem', color: approved ? '#2f855a' : rejected ? '#e53e3e' : '#718096', textTransform: 'uppercase', fontWeight: 700, textAlign: 'center', marginTop: 2 }}>
                                    {approved ? 'Approved' : rejected ? 'Rejected' : status}
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 6 }}>
                                    <button type="button" disabled={busyKey} onClick={() => handleGenerateLookbookV2(code)} style={{ gridColumn: 'span 2', fontSize: '0.68rem', padding: '4px 6px', border: '1px solid #bee3f8', borderRadius: 4, background: '#ebf8ff', color: '#2b6cb0' }}>
                                      {workflowBusy === `lookbook-generate-${code}` ? 'Generating...' : imageUrl ? 'Regenerate' : 'Generate'}
                                    </button>
                                    {imageUrl && !approved && (
                                      <button type="button" disabled={busyKey} onClick={() => handleReviewLookbookV2(code, 'approve')} style={{ fontSize: '0.68rem', padding: '4px 6px', border: '1px solid #c6f6d5', borderRadius: 4, background: '#f0fff4', color: '#2f855a' }}>Approve</button>
                                    )}
                                    {imageUrl && !rejected && (
                                      <button type="button" disabled={busyKey} onClick={() => handleReviewLookbookV2(code, 'reject')} style={{ fontSize: '0.68rem', padding: '4px 6px', border: '1px solid #fed7d7', borderRadius: 4, background: '#fff5f5', color: '#c53030' }}>Reject</button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          </>
                        )}
                      </div>
                    )}

                    {detailGenerated && isAdmin && (
                      <div className="ap-section" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#2d3748', fontWeight: 600 }}>Shopify Readiness</h3>
                        {readiness?.ready ? (() => {
                          const alreadyPushed = editPanelProduct.publishedAt || editPanelProduct.lastShopifySyncAt || editPanelProduct.status === 'published';
                          const isBusy = npActionBusy === `shopify-${editPanelProduct.sku}`;
                          return alreadyPushed ? (
                            <div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                {[
                                  { key: 'seo', label: 'SEO Title & Description' },
                                  { key: 'tags', label: 'Tags & Search Boost' },
                                  { key: 'metadata', label: 'Fabric Metadata' },
                                  { key: 'details', label: 'Product Title & HTML' },
                                ].map(scope => (
                                  <label key={scope.key} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: '#4a5568', cursor: 'pointer', background: shopifyUpdateScopes.includes(scope.key) ? '#ebf8ff' : '#f7fafc', border: `1px solid ${shopifyUpdateScopes.includes(scope.key) ? '#90cdf4' : '#e2e8f0'}`, borderRadius: 6, padding: '4px 8px' }}>
                                    <input type="checkbox" checked={shopifyUpdateScopes.includes(scope.key)} onChange={e => {
                                      setShopifyUpdateScopes(prev => e.target.checked ? [...prev, scope.key] : prev.filter(s => s !== scope.key));
                                    }} style={{ margin: 0 }} />
                                    {scope.label}
                                  </label>
                                ))}
                              </div>
                              <button type="button" className="ap-submit-btn" style={{ backgroundColor: '#38a169' }} disabled={isBusy || shopifyUpdateScopes.length === 0} onClick={() => handleSelectiveShopifyUpdate(editPanelProduct, shopifyUpdateScopes)}>
                                {isBusy ? 'Updating...' : `Update Shopify (${shopifyUpdateScopes.length} selected)`}
                              </button>
                            </div>
                          ) : (
                            <button type="button" className="ap-submit-btn" style={{ backgroundColor: '#38a169' }} disabled={isBusy} onClick={() => handleShopifyPipelineAction({ ...editPanelProduct, status: 'ready_for_shopify' })}>
                              {isBusy ? 'Publishing...' : 'Push to Shopify'}
                            </button>
                          );
                        })() : (
                          <div style={{ fontSize: '0.8rem', color: '#4a5568', background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: 6, padding: 10 }}>
                            <strong>Pending:</strong>
                            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                              {(readiness?.missing || ['Generate and approve at least one Lookbook image.']).map((m, idx) => <li key={idx}>{m}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              {workflowError && (
                <div style={{ marginTop: 12, padding: 10, borderRadius: 6, border: '1px solid #fed7d7', background: '#fff5f5', color: '#c53030', fontSize: '0.8rem' }}>
                  {workflowError}
                </div>
              )}

              {/* Inhouse AI Photoshoot Section */}
              {(() => {
                const reviewMap = editPanelProduct.aiPhotoshootReview || {};
                const generatedStyleKeys = new Set((editPanelProduct.photoshootImages || [])
                  .map(img => String(img.style || '').toUpperCase())
                  .filter(Boolean));
                const generatedStyles = [...generatedStyleKeys].map(key => {
                  const found = INHOUSE_AI_STYLES.find(s => s.key === key) || activeSwatchStyleKeys.find(s => s.key === key);
                  return found || { key, label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\B\w+/g, c => c.toLowerCase()) };
                });
                const newStyles = activeSwatchStyleKeys.filter(s => !generatedStyleKeys.has(s.key) && reviewMap[s.key] !== 'skipped');
                const allStyles = [...generatedStyles, ...newStyles];

                return allStyles.length > 0 ? (
                  <div className="ap-section" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
                    <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#2d3748', fontWeight: 600 }}>Inhouse AI Photoshoot</h3>
                    <p style={{ fontSize: '0.8rem', color: '#718096', margin: '0 0 0.75rem' }}>
                      Generated styles shown below. Regenerate individual styles or manage styles from settings.
                    </p>

                    <label className="ap-label">Styles</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', margin: '8px 0' }}>
                      {allStyles.map(style => {
                        const checked = inhouseAiSelectedStyles.includes(style.key);
                        const isGenerated = generatedStyleKeys.has(style.key);
                        const isSkipped = reviewMap[style.key] === 'skipped';
                        return (
                          <label key={style.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', background: '#f7fafc', padding: '6px 8px', borderRadius: 4, cursor: (isGenerated || isSkipped) ? 'default' : 'pointer', border: '1px solid #edf2f7', opacity: (isGenerated || isSkipped) ? 0.75 : 1 }}>
                            <input
                              type="checkbox"
                              checked={isGenerated || isSkipped || checked}
                              disabled={isGenerated || isSkipped}
                              onChange={() => {
                                if (checked) {
                                  setInhouseAiSelectedStyles(prev => prev.filter(s => s !== style.key));
                                } else {
                                  setInhouseAiSelectedStyles(prev => [...prev, style.key]);
                                }
                              }}
                            />
                            <span style={{ color: isGenerated ? '#a0aec0' : '#4a5568', textDecoration: isGenerated ? 'line-through' : 'none' }}>
                              {style.label}
                            </span>
                            {isGenerated && (
                              <span style={{ fontSize: '0.65rem', color: '#48bb78', fontWeight: 'bold', marginLeft: 'auto' }}>Generated</span>
                            )}
                            {isSkipped && (
                              <span style={{ fontSize: '0.65rem', color: '#718096', fontWeight: 'bold', marginLeft: 'auto' }}>Skipped</span>
                            )}
                            {!isGenerated && !isSkipped && canEdit && (
                              <button
                                type="button"
                                disabled={aiStyleActionBusy}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleReviewInhouseAiStyle(style.key, 'skip');
                                }}
                                style={{ marginLeft: 'auto', fontSize: '0.65rem', padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', color: '#4a5568', cursor: 'pointer' }}
                              >
                                Skip
                              </button>
                            )}
                          </label>
                        );
                      })}
                    </div>

                    <button
                      type="button"
                      className="ap-submit-btn"
                      style={{ backgroundColor: '#4c51bf', marginTop: '0.75rem' }}
                      disabled={inhouseAiBusy || editFabricPhotos.length === 0}
                      onClick={handleGenerateInhouseAi}
                    >
                      {inhouseAiBusy ? 'Generating with AI...' : `Generate${newStyles.length > 0 ? ` (${newStyles.length} new)` : ' Selected Styles'}`}
                    </button>
                    {editFabricPhotos.length === 0 && (
                      <p style={{ fontSize: '0.75rem', color: '#e53e3e', marginTop: 4 }}>
                        * Please upload at least one fabric photo and save changes first.
                      </p>
                    )}
                  </div>
                ) : null;
              })()}

              <button className="ap-submit-btn" style={{ marginTop: '1.5rem' }} disabled={editBusy || !!photoUploadProgress} onClick={handleEditSubmit}>
                {editBusy ? 'Saving...' : 'Save Batch Number'}
              </button>
            </div>
          </div>
        </>
      )}

      {sendPhotoshootOpen && (
        <>
          <div className="ap-overlay" onClick={() => setSendPhotoshootOpen(false)} />
          <div className="ap-panel">
            <div className="ap-panel-header">
              <h2>Send for Photoshoot</h2>
              <button className="ap-close-btn" onClick={() => setSendPhotoshootOpen(false)}>&times;</button>
            </div>
            <div className="ap-panel-body">
              <div className="ap-section">
                <label className="ap-label">Select Batch</label>
                <select value={sendPsBatch} onChange={e => handleSendPsBatchChange(e.target.value)}>
                  <option value="">-- Select Batch --</option>
                  {npBatchList.filter(b => (b.statusCounts || {}).photoshoot_pending > 0).map(b => (
                    <option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} ({b.statusCounts.photoshoot_pending} pending)</option>
                  ))}
                </select>
              </div>

              {sendPsFetchBusy && <p style={{ fontSize: '0.85rem', color: '#718096', padding: '0.5rem 0' }}>Loading pending products...</p>}

              {sendPsBatch && sendPsPendingProducts.length > 0 && (
                <div className="ap-section">
                  <label className="ap-label">Select Products to Send</label>
                  <div style={{ margin: '0.5rem 0' }}>
                    <label style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={sendPsPendingProducts.every(p => sendPsSelected[p.sku])}
                        onChange={e => {
                          const sel = { ...sendPsSelected };
                          sendPsPendingProducts.forEach(p => { sel[p.sku] = e.target.checked; });
                          setSendPsSelected(sel);
                        }}
                        style={{ marginRight: 6 }}
                      />
                      <strong>Select All</strong>
                    </label>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto' }}>
                    {sendPsPendingProducts.map(p => (
                      <label key={p.sku} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 6, background: sendPsSelected[p.sku] ? '#ebf8ff' : '#f7fafc', cursor: 'pointer', border: '1px solid #e2e8f0' }}>
                        <input
                          type="checkbox"
                          checked={!!sendPsSelected[p.sku]}
                          onChange={e => setSendPsSelected({ ...sendPsSelected, [p.sku]: e.target.checked })}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600 }}>{p.sku}</div>
                          <div style={{ fontSize: '0.8rem', color: '#718096' }}>{p.designNumber}</div>
                        </div>
                        {p.fabricPhotoUrl && <img src={p.fabricPhotoUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} />}
                      </label>
                    ))}
                  </div>
                  <button
                    className="ap-submit-btn"
                    disabled={sendPsBusy || Object.values(sendPsSelected).filter(Boolean).length === 0}
                    onClick={handleSendPhotoshootSubmit}
                  >
                    {sendPsBusy ? 'Sending...' : `Send ${Object.values(sendPsSelected).filter(Boolean).length} Product(s) for Photoshoot`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {printLabelsOpen && (
        <>
          <div className="ap-overlay" onClick={() => setPrintLabelsOpen(false)} />
          <div className="ap-panel">
            <div className="ap-panel-header">
              <h2>Print Batch Labels</h2>
              <button className="ap-close-btn" onClick={() => setPrintLabelsOpen(false)}>&times;</button>
            </div>
            <div className="ap-panel-body">
              <div className="ap-section">
                <label className="ap-label">Select Batch</label>
                <select value={printLabelsBatch} onChange={e => handlePrintLabelsBatchChange(e.target.value)}>
                  <option value="">-- Select Batch --</option>
                  {npBatchList.map(b => (
                    <option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} ({b.total} products)</option>
                  ))}
                </select>
              </div>

              <div className="ap-section">
                <label className="ap-label">Click a slot to set start position (ST-33 A4, 3×11)</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, maxWidth: 320, margin: '8px 0' }}>
                  {Array.from({ length: 33 }).map((_, i) => {
                    const slot = i + 1;
                    const isStart = slot === printLabelsStartSlot;
                    const isFill = slot >= printLabelsStartSlot && slot < printLabelsStartSlot + printLabelsSelectedCount;
                    const isUsed = slot < printLabelsStartSlot;
                    return (
                      <div
                        key={slot}
                        onClick={() => setPrintLabelsStartSlot(slot)}
                        style={{
                          minHeight: 26,
                          border: isStart ? '2px solid #0ea5e9' : '1px solid #cbd5e1',
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 850,
                          cursor: 'pointer',
                          color: isFill ? '#075985' : isUsed ? '#a0aec0' : '#64748b',
                          background: isFill ? '#e0f2fe' : isUsed ? '#f1f5f9' : '#fff',
                        }}
                      >
                        {slot}
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#718096' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 2, verticalAlign: 'middle', marginRight: 4 }} /> Used
                  <span style={{ display: 'inline-block', width: 12, height: 12, background: '#e0f2fe', border: '1px solid #38bdf8', borderRadius: 2, verticalAlign: 'middle', marginLeft: 12, marginRight: 4 }} /> Will print
                  <span style={{ display: 'inline-block', width: 12, height: 12, background: '#fff', border: '2px solid #0ea5e9', borderRadius: 2, verticalAlign: 'middle', marginLeft: 12, marginRight: 4 }} /> Start
                </div>
              </div>

              {printLabelsFetchBusy && <p style={{ fontSize: '0.85rem', color: '#718096', padding: '0.5rem 0' }}>Loading products...</p>}

              {printLabelsBatch && printLabelsProducts.length > 0 && (
                <div className="ap-section">
                  <label className="ap-label">Select Products to Print</label>
                  <div style={{ margin: '0.5rem 0' }}>
                    <label style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={printLabelsProducts.every(p => printLabelsSelected[p.sku])}
                        onChange={e => {
                          const sel = { ...printLabelsSelected };
                          printLabelsProducts.forEach(p => { sel[p.sku] = e.target.checked; });
                          setPrintLabelsSelected(sel);
                        }}
                        style={{ marginRight: 6 }}
                      />
                      <strong>Select All ({printLabelsProducts.length})</strong>
                    </label>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 350, overflowY: 'auto' }}>
                    {printLabelsProducts.map(p => (
                      <label key={p.sku} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 6, background: printLabelsSelected[p.sku] ? '#ebf8ff' : '#f7fafc', cursor: 'pointer', border: '1px solid #e2e8f0' }}>
                        <input
                          type="checkbox"
                          checked={!!printLabelsSelected[p.sku]}
                          onChange={e => setPrintLabelsSelected({ ...printLabelsSelected, [p.sku]: e.target.checked })}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600 }}>{p.sku}</div>
                          <div style={{ fontSize: '0.8rem', color: '#718096' }}>
                            {p.designNumber}{(p.pieceIds || []).length > 0 ? ` • ${p.pieceIds[0]}` : ''}
                          </div>
                        </div>
                        {p.fabricPhotoUrl && <img src={p.fabricPhotoUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} />}
                      </label>
                    ))}
                  </div>

                  {printLabelsSelectedCount > 0 && (
                    <div style={{ fontSize: '0.8rem', color: '#4a5568', margin: '10px 0 4px', padding: '8px 10px', background: '#f7fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                      {printLabelsSelectedCount} label(s) starting at slot {printLabelsStartSlot} → next slot will be {printLabelsStartSlot + printLabelsSelectedCount > 33 ? 1 : printLabelsStartSlot + printLabelsSelectedCount}
                      {printLabelsSelectedCount > (34 - printLabelsStartSlot) && (
                        <span style={{ color: '#c53030', fontWeight: 600 }}> — will overflow to {Math.ceil((printLabelsSelectedCount - (34 - printLabelsStartSlot)) / 33) + 1} sheets</span>
                      )}
                    </div>
                  )}

                  <button
                    className="ap-submit-btn"
                    disabled={printLabelsBusy || printLabelsSelectedCount === 0}
                    onClick={handlePrintLabelsSubmit}
                    style={{ marginTop: 12 }}
                  >
                    {printLabelsBusy ? 'Opening...' : `Print ${printLabelsSelectedCount} Label(s) — A4 Sheet`}
                  </button>
                </div>
              )}

              {printLabelsBatch && !printLabelsFetchBusy && printLabelsProducts.length === 0 && (
                <p style={{ fontSize: '0.85rem', color: '#718096', padding: '0.5rem 0' }}>No products in this batch.</p>
              )}
            </div>
          </div>
        </>
      )}

      {photoshootPanelProduct && (
        <>
          <div className="ap-overlay" onClick={() => setPhotoshootPanelProduct(null)} />
          <div className="ap-panel">
            <div className="ap-panel-header">
              <h2>Photoshoot — {photoshootPanelProduct.sku}</h2>
              <button className="ap-close-btn" onClick={() => setPhotoshootPanelProduct(null)}>&times;</button>
            </div>
            <div className="ap-panel-body">
              <div className="ap-section">
                <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Design: {photoshootPanelProduct.designNumber}</p>
                <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#718096' }}>
                  {(photoshootPanelProduct.photoshootImages || []).length} image(s) uploaded
                  {photoshootPanelProduct.photoshootFirstUploadAt && (
                    <> &middot; Upload window: 24hrs from first upload</>
                  )}
                </p>
              </div>

              {(photoshootPanelProduct.photoshootImages || []).length > 0 && (
                <div className="ap-section">
                  <label className="ap-label">Uploaded Images</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                    {photoshootPanelProduct.photoshootImages.map((img, i) => (
                      <div key={i} style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img
                          src={img.url}
                          alt={`shoot-${i}`}
                          title={`Click to open ${img.style || 'photoshoot'} image in new tab`}
                          onClick={() => window.open(img.url, '_blank')}
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', cursor: 'pointer' }}
                        />
                        <span style={{ display: 'block', fontSize: '0.65rem', color: '#718096', textAlign: 'center', marginTop: '4px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {img.style ? img.style.replace('_', ' ') : `shoot-${i}`}
                        </span>
                        <button
                          onClick={() => handleDeletePhotoshootImage(i)}
                          style={{ position: 'absolute', top: -6, right: -6, background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 12, cursor: 'pointer', lineHeight: '18px' }}
                          title="Delete image"
                        >&times;</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="ap-section">
                <label className="ap-label">Add Image</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={psUploadBusy}
                  onChange={e => {
                    const files = e.target.files;
                    if (files && files.length > 0) handlePhotoshootUpload(files);
                    e.target.value = '';
                  }}
                />
                {psUploadBusy && <p style={{ fontSize: '0.85rem', color: '#3182ce', marginTop: 4 }}>{psUploadProgress || 'Uploading...'}</p>}
              </div>
            </div>
          </div>
        </>
      )}

      {swatchPromptsOpen && (
        <>
          <div className="ap-overlay" onClick={() => setSwatchPromptsOpen(false)} />
          <div className="ap-panel" style={{ maxWidth: 700 }}>
            <div className="ap-panel-header">
              <h2>AI Photoshoot Prompts</h2>
              <button className="ap-close-btn" onClick={() => setSwatchPromptsOpen(false)}>&times;</button>
            </div>
            <div className="ap-panel-body">
              {swatchPromptsBusy && swatchStyles.length === 0 ? (
                <p style={{ color: '#718096', padding: '1rem 0' }}>Loading prompts...</p>
              ) : (
                <>
                  <p style={{ fontSize: '0.8rem', color: '#718096', margin: '0 0 1rem' }}>
                    Manage AI image styles below. Add, edit, remove, or reorder. Changes apply to all future generations.
                  </p>
                  <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid #4c51bf', paddingBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <label style={{ fontWeight: 700, fontSize: '0.95rem', color: '#4c51bf' }}>Common Instructions</label>
                      {swatchDefaultPreamble && swatchPreamble !== swatchDefaultPreamble && (
                        <button
                          type="button"
                          onClick={() => setSwatchPreamble(swatchDefaultPreamble)}
                          style={{ fontSize: '0.7rem', padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', color: '#718096', cursor: 'pointer' }}
                        >
                          Reset to Default
                        </button>
                      )}
                    </div>
                    <p style={{ fontSize: '0.75rem', color: '#a0aec0', margin: '0 0 6px' }}>
                      Sent before every style prompt. Placeholder: {'{FABRIC_NAME}'} (replaced with actual fabric name)
                    </p>
                    <textarea
                      value={swatchPreamble}
                      onChange={e => setSwatchPreamble(e.target.value)}
                      rows={10}
                      style={{ width: '100%', padding: 8, fontSize: '0.8rem', border: '1px solid #cbd5e0', borderRadius: 6, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <label style={{ fontWeight: 700, fontSize: '0.95rem', color: '#2d3748' }}>Image Styles ({swatchStyles.length})</label>
                    <button
                      type="button"
                      onClick={addSwatchStyle}
                      style={{ fontSize: '0.8rem', padding: '4px 12px', border: '1px solid #4c51bf', borderRadius: 4, background: '#fff', color: '#4c51bf', cursor: 'pointer', fontWeight: 600 }}
                    >
                      + Add Style
                    </button>
                  </div>
                  {swatchStyles.map((style, idx) => (
                    <div key={idx} style={{ marginBottom: '1.25rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <button type="button" onClick={() => moveSwatchStyle(idx, -1)} disabled={idx === 0}
                            style={{ fontSize: '0.65rem', padding: '1px 4px', border: '1px solid #e2e8f0', borderRadius: 3, background: '#fff', color: idx === 0 ? '#cbd5e0' : '#718096', cursor: idx === 0 ? 'default' : 'pointer', lineHeight: 1 }}>&#9650;</button>
                          <button type="button" onClick={() => moveSwatchStyle(idx, 1)} disabled={idx === swatchStyles.length - 1}
                            style={{ fontSize: '0.65rem', padding: '1px 4px', border: '1px solid #e2e8f0', borderRadius: 3, background: '#fff', color: idx === swatchStyles.length - 1 ? '#cbd5e0' : '#718096', cursor: idx === swatchStyles.length - 1 ? 'default' : 'pointer', lineHeight: 1 }}>&#9660;</button>
                        </div>
                        <input
                          type="text"
                          placeholder="Style Name (e.g. Fabric Editorial)"
                          value={style.label || ''}
                          onChange={e => updateSwatchStyle(idx, 'label', e.target.value)}
                          style={{ flex: 1, padding: '4px 8px', fontSize: '0.85rem', fontWeight: 600, border: '1px solid #cbd5e0', borderRadius: 4, color: '#2d3748' }}
                        />
                        <span style={{ fontSize: '0.7rem', color: '#a0aec0', minWidth: 80 }}>{style.key || '—'}</span>
                        <button type="button" onClick={() => removeSwatchStyle(idx)}
                          style={{ fontSize: '0.75rem', padding: '2px 8px', border: '1px solid #fed7d7', borderRadius: 4, background: '#fff5f5', color: '#c53030', cursor: 'pointer' }}>
                          Remove
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', color: style.autoGenerate !== false ? '#38a169' : '#a0aec0', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={style.autoGenerate !== false}
                            onChange={e => updateSwatchStyle(idx, 'autoGenerate', e.target.checked)}
                          />
                          Auto-generate for new products
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', color: '#4a5568' }}>
                          Shopify Image Position:
                          <input
                            type="number"
                            min="1"
                            max="20"
                            value={style.shopifyPosition || ''}
                            onChange={e => updateSwatchStyle(idx, 'shopifyPosition', e.target.value ? Number(e.target.value) : null)}
                            placeholder="—"
                            style={{ width: 50, padding: '2px 6px', fontSize: '0.8rem', border: '1px solid #cbd5e0', borderRadius: 4, textAlign: 'center' }}
                          />
                        </label>
                      </div>
                      <textarea
                        value={style.prompt || ''}
                        onChange={e => updateSwatchStyle(idx, 'prompt', e.target.value)}
                        placeholder="Enter the prompt for this style..."
                        rows={6}
                        style={{ width: '100%', padding: 8, fontSize: '0.8rem', border: '1px solid #cbd5e0', borderRadius: 6, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                      />
                    </div>
                  ))}
                  {swatchStyles.length === 0 && (
                    <p style={{ color: '#a0aec0', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
                      No styles configured. Click "+ Add Style" to create one.
                    </p>
                  )}
                  <button
                    type="button"
                    className="ap-submit-btn"
                    style={{ backgroundColor: '#4c51bf' }}
                    disabled={swatchPromptsBusy}
                    onClick={handleSaveSwatchPrompts}
                  >
                    {swatchPromptsBusy ? 'Saving...' : 'Save All Prompts'}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ProductsPage;
